import CompanyOptionLabel from "../CompanyOptionLabel";
// src/components/PopupWindows/AddJobDialog.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  createTeamMemberIfMissing,
  fetchTeamMembers,
  normalizeTeamMemberName,
  upsertTeamMemberOption,
} from "../../utils/teamMembers";
import { searchPlatformContacts } from "../../utils/hiringManagers";
import InlineLinkEntryCard from "./common/InlineLinkEntryCard";
import LocationSearchableField from "../LocationSearchableField";
import { deriveLinkAutofill, describeLinkAutofill } from "../../utils/linkAutofill";
import {
  dedupeAutocompleteOptions,
  normalizeAutocompleteText,
} from "../../utils/autocompleteOptions";
import {
  JOB_EMPLOYMENT_TYPE_OPTIONS,
  normalizeEmploymentType,
} from "../../utils/jobEmploymentTypes";
import { normalizeLocationValueForSave } from "../../utils/locationFilters";

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}
const companyFilter = createFilterOptions();
const hiringManagerFilter = createFilterOptions();
const teamMemberFilter = createFilterOptions();
const COMPANY_QUICK_ACTION_JOB_STATUS_OPTIONS = [
  "Draft",
  "Accepting Candidates",
  "Placed",
  "Open",
  "On Hold",
  "For Review",
  "Closed",
  "Pending",
  "Approved",
  "Rejected",
  "Completed",
  "Scheduled",
  "Archived",
  "In Progress",
];

function safeDecodeJwt(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const pillBtn = {
  borderRadius: 999,
  textTransform: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const primaryPurpleBtn = {
  ...pillBtn,
  px: 3,
  fontWeight: 800,
  backgroundColor: "#7c3aed",
  boxShadow: "0 10px 25px rgba(124,58,237,0.22)",
  "&:hover": { backgroundColor: "#6d28d9" },
};

const backPurpleBtn = {
  ...pillBtn,
  px: 2.75,
  fontWeight: 800,
  backgroundColor: "#7c3aed",
  color: "#fff",
  "&:hover": { backgroundColor: "#6d28d9" },
};

const outlinePillBtn = {
  ...pillBtn,
  px: 2.25,
  fontWeight: 800,
  borderColor: "#c4b5fd",
  color: "#6d28d9",
  "&:hover": { borderColor: "#a78bfa", backgroundColor: "#faf5ff" },
};

function StepDot({ active }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: 999,
        background: active ? "#7c3aed" : "#e5e7eb",
      }}
    />
  );
}

function CardAction({ icon, title, subtitle, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: 2.5,
        p: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        cursor: "pointer",
        transition: "all 120ms ease",
        "&:hover": {
          borderColor: "#c4b5fd",
          boxShadow: "0 10px 30px rgba(124,58,237,0.12)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(135deg,#eef2ff,#f5f3ff)",
          color: "#4f46e5",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

/** ---- Shared field styles (matches screenshots) ---- */
const overviewTitleSx = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "24px",
  color: "#3451F4",
  mt: 0.25,
  mb: 0.35,
};

const overviewLabelSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#475467",
  mb: 0.6,
  lineHeight: 1,
};

const overviewOptionalSx = {
  color: "#98A2B3",
  fontWeight: 500,
};

const overviewFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: "10px",
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#BFC6D4" },
    "&.Mui-focused fieldset": {
      borderColor: "#A78BFA",
      boxShadow: "0 0 0 3px rgba(167,139,250,0.12)",
    },
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    fontWeight: 500,
    color: "#344054",
    py: 1.15,
  },
  "& .MuiAutocomplete-input": {
    fontSize: 14,
    fontWeight: 500,
    color: "#344054",
  },
};

const overviewBackBtnSx = {
  ...backPurpleBtn,
  minWidth: 88,
  px: 2.5,
  gap: 0.75,
  boxShadow: "0 10px 24px rgba(124,58,237,0.22)",
  "&:hover": {
    background: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    color: "#9C4FFF !important",
    border: "1px solid #9C4FFF !important",
    borderColor: "#9C4FFF !important",
    boxShadow: "none !important",
  },
};

const overviewPrimaryBtnSx = {
  ...primaryPurpleBtn,
  minWidth: 88,
  px: 2.5,
  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
  boxShadow: "0 10px 24px rgba(124,58,237,0.22)",
  "&:hover": {
    background: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    color: "#9C4FFF !important",
    border: "1px solid #9C4FFF !important",
    borderColor: "#9C4FFF !important",
    boxShadow: "none !important",
  },
};

const overviewOutlineBtnSx = {
  ...outlinePillBtn,
  minWidth: 88,
  px: 2.1,
  borderColor: "#C4B5FD",
  color: "#9333EA",
  backgroundColor: "#fff",
};

const dialogFooterScrollSx = {
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
};

const dialogFooterRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
  flexWrap: "nowrap",
};

const dialogFooterActionGroupSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  flexWrap: "nowrap",
  justifyContent: "flex-end",
};

const dialogFooterCompactActionGroupSx = {
  ...dialogFooterActionGroupSx,
  gap: 1,
};

const roleTextareaSx = {
  ...overviewFieldSx,
  "& .MuiOutlinedInput-root": {
    ...overviewFieldSx["& .MuiOutlinedInput-root"],
    minHeight: "unset",
    alignItems: "flex-start",
    paddingTop: 0,
    paddingBottom: 0,
  },
  "& textarea": {
    fontSize: 14,
    fontWeight: 500,
    color: "#344054",
    lineHeight: 1.65,
  },
};

const roleChipSx = {
  height: 22,
  borderRadius: 999,
  backgroundColor: "#EFF4FF",
  color: "#3451F4",
  fontSize: 11,
  fontWeight: 700,
  "& .MuiChip-label": {
    px: 1,
  },
  "& .MuiChip-deleteIcon": {
    color: "#3451F4",
    fontSize: 16,
    mr: 0.25,
  },
};

const roleActionLinkSx = {
  fontSize: 13,
  fontWeight: 700,
  color: "#9C4FFF",
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover": { textDecoration: "underline" },
};

const roleHelperTextSx = {
  fontSize: 12,
  fontWeight: 500,
  color: "#667085",
  mt: 0.5,
};

const fieldErrorTextSx = {
  ...roleHelperTextSx,
  color: "#D92D20",
};

const REQUIRED_FIELD_LABELS = {
  postLink: "Job link",
  uploadFile: "Job file",
  jobTitle: "Job Title",
  company: "Company (Client)",
  hiringManager: "Hiring Manager/Contact",
  candidateStage: "Stage",
  description: "Description",
};

const REQUIRED_FIELD_KEYS_BY_SCOPE = {
  link: ["postLink"],
  upload: ["uploadFile"],
  quickCreate: ["jobTitle", "company"],
  overview: ["jobTitle", "company", "hiringManager", "candidateStage"],
  role: ["description"],
  all: ["jobTitle", "company", "hiringManager", "candidateStage", "description"],
};

function buildRequiredFieldError(fieldKey) {
  return `${REQUIRED_FIELD_LABELS[fieldKey]} is required.`;
}

const detailDocumentCardSx = {
  flex: 1,
  borderRadius: "10px",
  border: "1.5px solid #A78BFA",
  backgroundColor: "#fff",
  px: 1.75,
  py: 1.35,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.25,
};

const detailDocumentIconWrapSx = {
  width: 28,
  height: 28,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  color: "#9C4FFF",
  backgroundColor: "#FAF5FF",
  flex: "0 0 auto",
};

const linkLabelSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#475467",
  mb: 0.85,
  lineHeight: 1.2,
};

const linkFieldSx = {
  ...overviewFieldSx,
  "& .MuiOutlinedInput-root": {
    ...overviewFieldSx["& .MuiOutlinedInput-root"],
    minHeight: 40,
  },
  "& .MuiOutlinedInput-input": {
    ...overviewFieldSx["& .MuiOutlinedInput-input"],
    py: 1,
  },
  "& .MuiAutocomplete-clearIndicator": {
    display: "none",
  },
};

const linkSplitFieldWrapSx = {
  display: "flex",
  alignItems: "stretch",
  border: "1px solid #D0D5DD",
  borderRadius: "10px",
  backgroundColor: "#fff",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  overflow: "hidden",
  transition: "box-shadow 120ms ease, border-color 120ms ease",
  "&:focus-within": {
    borderColor: "#A78BFA",
    boxShadow: "0 0 0 3px rgba(167,139,250,0.12)",
  },
};

const linkSplitProtocolSx = {
  minWidth: 64,
  px: 1.75,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRight: "1px solid #D0D5DD",
  color: "#667085",
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

function DownCaretAdornment() {
  return (
    <InputAdornment position="end">
      <KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />
    </InputAdornment>
  );
}

function prettyBytes(bytes) {
  const n = Number(bytes || 0);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function normalizeUniqueStrings(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [values])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function normalizeLookupValue(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function normalizeCompanyName(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function normalizeHiringManagerValue(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function getUrlHostname(value = "") {
  try {
    return new URL(String(value || "")).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
}

function splitHttpUrl(value = "", fallbackProtocol = "http://") {
  const trimmed = String(value || "").trim();
  if (!trimmed) return { protocol: fallbackProtocol, value: "" };

  if (/^https?:\/\//i.test(trimmed)) {
    return {
      protocol: trimmed.toLowerCase().startsWith("https://") ? "https://" : "http://",
      value: trimmed.replace(/^https?:\/\//i, ""),
    };
  }

  return { protocol: fallbackProtocol, value: trimmed };
}

function buildHttpUrl(protocol = "http://", value = "") {
  const trimmed = String(value || "").trim();
  return trimmed ? `${protocol}${trimmed}` : "";
}

function createEmptyOtherLink() {
  return { protocol: "http://", value: "", label: "" };
}

function createEmptyCompanyProfile() {
  return {
    company_name: "",
    company_website: "",
    company_career_website: "",
    company_industry: "",
    company_size: "",
    company_description: "",
    company_logo_url: "",
    source: "",
  };
}

function normalizeQuickActionJobStatus(value, fallback = "Draft") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;

  const matchingOption = COMPANY_QUICK_ACTION_JOB_STATUS_OPTIONS.find(
    (option) => option.toLowerCase() === raw.toLowerCase()
  );

  return matchingOption || raw;
}

function toLinkState(url = "", label = "") {
  const parts = splitHttpUrl(url, "https://");
  return { protocol: parts.protocol, value: parts.value, label: String(label || "").trim() };
}

function findMatchingCompanyOption(options = [], { companyName = "", website = "" } = {}) {
  const normalizedName = normalizeLookupValue(companyName);
  const websiteHost = getUrlHostname(website);

  if (websiteHost) {
    const byWebsite = options.find(
      (option) => getUrlHostname(option?.website || "") === websiteHost
    );
    if (byWebsite) return byWebsite;
  }

  if (!normalizedName) return null;

  const exact = options.find(
    (option) => normalizeLookupValue(option?.company_name || "") === normalizedName
  );
  if (exact) return exact;

  return (
    options.find((option) => {
      const optionName = normalizeLookupValue(option?.company_name || "");
      return optionName && (optionName.includes(normalizedName) || normalizedName.includes(optionName));
    }) || null
  );
}

function buildCompanyOption(raw = {}) {
  const company_id = Number(raw?.company_id ?? raw?.companyId ?? raw?.id);
  const company_name = normalizeCompanyName(
    raw?.company_name ?? raw?.companyName ?? raw?.name ?? ""
  );
  if (!company_name) return null;

  return {
    company_id: Number.isFinite(company_id) && company_id > 0 ? company_id : null,
    company_name,
    company_logo: String(
      raw?.company_logo ?? raw?.company_logo_url ?? raw?.companyLogo ?? raw?.logo ?? ""
    ).trim(),
    website: String(raw?.company_website ?? raw?.companyWebsite ?? raw?.website ?? "").trim(),
    industry: String(raw?.company_industry ?? raw?.industry ?? "").trim(),
    size_range: String(raw?.company_size ?? raw?.size_range ?? raw?.sizeRange ?? "").trim(),
    career_site: String(
      raw?.company_career_website ?? raw?.career_website ?? raw?.career_site ?? raw?.careerSite ?? ""
    ).trim(),
    full_company_description: String(
      raw?.company_description ?? raw?.full_company_description ?? raw?.companyDescription ?? ""
    ).trim(),
    source: String(raw?.source ?? "").trim(),
    status: String(raw?.status ?? "").trim(),
  };
}

function upsertCompanyOption(options = [], nextOption = null) {
  if (!nextOption?.company_name) return Array.isArray(options) ? options : [];

  const normalizedName = normalizeLookupValue(nextOption.company_name);
  const nextList = Array.isArray(options) ? [...options] : [];
  const matchIndex = nextList.findIndex((option) => {
    if (
      nextOption.company_id != null &&
      option?.company_id != null &&
      String(option.company_id) === String(nextOption.company_id)
    ) {
      return true;
    }

    return normalizeLookupValue(option?.company_name || "") === normalizedName;
  });

  if (matchIndex >= 0) {
    nextList[matchIndex] = {
      ...nextList[matchIndex],
      ...nextOption,
    };
  } else {
    nextList.push(nextOption);
  }

  return nextList.sort((a, b) =>
    String(a?.company_name || "").localeCompare(String(b?.company_name || ""))
  );
}

function getCompanyOptionLabel(option) {
  if (typeof option === "string") return option;
  if (option?.inputValue) return option.inputValue;
  return String(option?.company_name || "");
}

function buildHiringManagerOption(raw = {}) {
  const safeRaw =
    typeof raw === "string"
      ? { name: raw }
      : raw && typeof raw === "object"
        ? raw
        : {};
  const id = Number(safeRaw?.id ?? safeRaw?.hiring_manager_id ?? safeRaw?.hiringManagerId);
  const name = normalizeHiringManagerValue(
    safeRaw?.name ??
      safeRaw?.label ??
      safeRaw?.hiring_manager_name ??
      safeRaw?.contact_name ??
      safeRaw?.inputValue ??
      ""
  );
  if (!name) return null;

  return {
    id: Number.isFinite(id) && id > 0 ? id : null,
    name,
    label: name,
    role: normalizeHiringManagerValue(
      safeRaw?.role ?? safeRaw?.title ?? safeRaw?.position ?? ""
    ),
    email: String(safeRaw?.email ?? safeRaw?.contact_email ?? "").trim(),
    phone: String(safeRaw?.phone ?? safeRaw?.contact_phone ?? "").trim(),
    profile_image: String(
      safeRaw?.profile_image ??
        safeRaw?.avatar_url ??
        safeRaw?.avatar ??
        safeRaw?.photo_url ??
        safeRaw?.image ??
        ""
    ).trim(),
    contact_kind: String(safeRaw?.contact_kind ?? safeRaw?.contactKind ?? "").trim(),
  };
}

function getHiringManagerOptionKey(option = {}) {
  const normalizedName = normalizeLookupValue(option?.name || option?.label || "");
  const normalizedRole = normalizeLookupValue(option?.role || option?.title || "");
  const normalizedEmail = String(option?.email || "").trim().toLowerCase();

  if (option?.id != null && option.id !== "") return `id:${option.id}`;
  if (normalizedEmail) return `email:${normalizedEmail}`;
  return `${normalizedName}::${normalizedRole}`;
}

function mergeHiringManagerOptions(optionLists = []) {
  const seen = new Set();
  const nextOptions = [];

  (Array.isArray(optionLists) ? optionLists : [])
    .flatMap((list) => (Array.isArray(list) ? list : [list]))
    .forEach((item) => {
      const option = buildHiringManagerOption(item);
      if (!option) return;

      const key = getHiringManagerOptionKey(option);
      if (!key || seen.has(key)) return;
      seen.add(key);
      nextOptions.push(option);
    });

  return nextOptions;
}

function buildCompanyHiringManagerOptions(company = {}) {
  return mergeHiringManagerOptions([
    Array.isArray(company?.contacts) ? company.contacts : [],
    Array.isArray(company?.hiringManagers)
      ? company.hiringManagers
      : Array.isArray(company?.hiring_managers)
        ? company.hiring_managers
        : [],
    {
      name: company?.contact_name || company?.contact_email || company?.contact_phone || "",
      role: company?.contact_role || company?.contact_title || "Primary Contact",
      email: company?.contact_email || "",
      phone: company?.contact_phone || "",
      contact_kind: "contact",
    },
    {
      name: company?.hiring_manager_name || company?.hiring_manager || "",
      role: company?.hiring_manager_role || company?.hiring_manager_title || "Hiring Manager",
      contact_kind: "hiring_manager",
    },
  ]);
}

function InfoRow({ jobTitle, companyOption, companyInputValue, hiringManagerName, jobLocation, workArrangement, employment, source, slots }) {
  const parts = [
    jobTitle?.trim() || "",
    companyOption?.company_name || companyInputValue || "",
    hiringManagerName?.trim() || "",
    jobLocation?.trim() || "",
    workArrangement || "",
    employment || "",
    source || "",
    slots ? `${slots} slots` : "",
  ].filter(Boolean);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 900, color: "#3451F4" }}>
        Info:
      </Typography>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        {parts.join("  |  ")}
      </Typography>
    </Box>
  );
}

function Header({ dialogTitle, screen, wizardStep, onClose, showWizardSteps = true }) {
  return (
    <DialogTitle data-popup-header
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 4,
        pt: 4,
        pb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 28, height: 28, display: "grid", placeItems: "center", color: "#3451F4" }}>
          <WorkOutlineRoundedIcon />
        </Box>
        <Typography data-popup-title variant="h6" sx={{ fontSize: 20, fontWeight: 400, lineHeight: "35px" }}>
          {dialogTitle}
        </Typography>
      </Box>

      <Box data-popup-header sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {screen === "wizard" && showWizardSteps ? (
          <Box sx={{ display: "flex", gap: 0.75, mr: 0.5 }}>
            <StepDot active={wizardStep === 0} />
            <StepDot active={wizardStep === 1} />
            <StepDot active={wizardStep === 2} />
          </Box>
        ) : null}

        <IconButton data-popup-close
          onClick={onClose}
          size="small"
          sx={{
            width: 30,
            height: 30,
            borderRadius: 999,
            backgroundColor: "#7c3aed",
            color: "#fff",
            "&:hover": { backgroundColor: "#6d28d9" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </DialogTitle>
  );
}

export default function AddJobDialog({
  open,
  onClose,
  onCreated,
  user,
  initialValues = null,
  initialScreen = "menu",
  initialWizardStep = 0,
  candidateStageOptions = [],
  initialCandidateStage = "",
  openJobOnComplete = true,
  createButtonLabel = "Create Job",
  variant = "default",
}) {
  const WORK_ARRANGEMENT_OPTIONS = ["Remote", "Hybrid", "On-site"];
  const LINK_SOURCE_OPTIONS = [
    { value: "", label: "None" },
    { value: "Job Board", label: "Job Board" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "Indeed", label: "Indeed" },
    { value: "Greenhouse", label: "Greenhouse" },
    { value: "Lever", label: "Lever" },
    { value: "Workable", label: "Workable" },
    { value: "Ashby", label: "Ashby" },
    { value: "Jobvite", label: "Jobvite" },
    { value: "ZipRecruiter", label: "ZipRecruiter" },
    { value: "SmartRecruiters", label: "SmartRecruiters" },
    { value: "Monster", label: "Monster" },
    { value: "Company Website", label: "Company Website" },
    { value: "Referral", label: "Referral" },
  ];
  const SLOT_OPTIONS = Array.from({ length: 20 }, (_, index) => String(index + 1));
  const normalizedCandidateStageOptions = useMemo(
    () =>
      (Array.isArray(candidateStageOptions) ? candidateStageOptions : [])
        .map((option) => {
          if (typeof option === "string") {
            const label = option.trim();
            return label ? { value: label, label } : null;
          }

          const value = String(option?.value || option?.label || "").trim();
          const label = String(option?.label || option?.value || "").trim();
          return value && label ? { value, label } : null;
        })
        .filter(Boolean),
    [candidateStageOptions]
  );
  const showCandidateStageField = normalizedCandidateStageOptions.length > 0;
  const isCompanyQuickAction = variant === "companyQuickAction";

  // screens: menu -> link/upload -> wizard (step0..2)
  const [screen, setScreen] = useState("menu"); // "menu" | "link" | "upload" | "wizard"
  const [wizardStep, setWizardStep] = useState(0);

  // overview
  const [jobTitle, setJobTitle] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [employment, setEmployment] = useState("");
  const [workArrangement, setWorkArrangement] = useState("");
  const [jobLocation, setJobLocation] = useState("");
  const [hiringManagerName, setHiringManagerName] = useState("");
  const [slots, setSlots] = useState("");
  const [candidateStage, setCandidateStage] = useState("");

  // company from companies table (via API)
  const [companyOption, setCompanyOption] = useState(null); // {company_id, company_name, company_logo}
  const [companyInputValue, setCompanyInputValue] = useState("");

  // link/upload pre-step
  const [postLink, setPostLink] = useState("");
  const [source, setSource] = useState("");
  const [uploadFile, setUploadFile] = useState(null); // optional: used in Step 1 now (upload a file)

  // (keep existing state but Step 1 UI is now “candidate-style fields”)
  const [description, setDescription] = useState(""); // not shown in Step 1 anymore (kept so payload still works if you want later)
  const [skills, setSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [softSkillInput, setSoftSkillInput] = useState("");
  const [compensation, setCompensation] = useState(""); // not shown in Step 1 anymore (kept)
  const [responsibilities, setResponsibilities] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [educationRequirements, setEducationRequirements] = useState("");
  const [experienceRequirements, setExperienceRequirements] = useState("");
  const [interviewProcess, setInterviewProcess] = useState([]); // not shown in Step 1 anymore (kept)
  const [interviewInput, setInterviewInput] = useState("");
  const [parsedCompanyProfile, setParsedCompanyProfile] = useState(() =>
    createEmptyCompanyProfile()
  );

  // Step 1 “candidate-style” extra fields
  const [postProtocol, setPostProtocol] = useState("http://");
  const [postUrlValue, setPostUrlValue] = useState("");
  const [websiteProtocol, setWebsiteProtocol] = useState("http://");
  const [websiteUrlValue, setWebsiteUrlValue] = useState("");
  const [careerWebsite, setCareerWebsite] = useState("");
  const [otherLinks, setOtherLinks] = useState([createEmptyOtherLink()]);

  // additional details
  const [notes, setNotes] = useState("");
  const [assignedTeamMember, setAssignedTeamMember] = useState("");
  const [documents, setDocuments] = useState([]); // {name, file, addedAt}
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamMembersDebug, setTeamMembersDebug] = useState("");

  // companies list
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [defaultHiringManagerOptions, setDefaultHiringManagerOptions] = useState([]);
  const [companyHiringManagerOptions, setCompanyHiringManagerOptions] = useState([]);
  const [platformHiringManagerOptions, setPlatformHiringManagerOptions] = useState([]);
  const [manualHiringManagerOptions, setManualHiringManagerOptions] = useState([]);
  const [loadingOverviewOptions, setLoadingOverviewOptions] = useState(false);
  const [loadingCompanyHiringManagers, setLoadingCompanyHiringManagers] = useState(false);
  const [loadingPlatformHiringManagers, setLoadingPlatformHiringManagers] = useState(false);

  // submit
  const [saving, setSaving] = useState(false);
  const [parsingLink, setParsingLink] = useState(false);
  const [processingUpload, setProcessingUpload] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const docsInputRef = useRef(null);

  const navigate = useNavigate();

  const notifyJobCreated = useCallback((job) => {
    const jobId = Number(job?.id ?? job?.job_id ?? 0);
    const companyId = Number(job?.company_id ?? job?.companyId ?? 0);
    if (typeof window === "undefined" || !Number.isFinite(jobId) || jobId <= 0) return;

    window.dispatchEvent(
      new CustomEvent("job-created", {
        detail: {
          job,
          jobId,
          companyId: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
        },
      })
    );
  }, []);

  const setScopedFieldErrors = useCallback((scope, nextScopedErrors = {}) => {
    const scopedKeys = REQUIRED_FIELD_KEYS_BY_SCOPE[scope] || [];
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      scopedKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(next, key) && !nextScopedErrors[key]) {
          delete next[key];
          changed = true;
        }
      });

      Object.entries(nextScopedErrors).forEach(([key, value]) => {
        if (next[key] !== value) {
          next[key] = value;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, []);

  const clearFieldError = useCallback((fieldKey) => {
    setFieldErrors((prev) => {
      if (!prev?.[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  }, []);

  const ownerName = useMemo(() => {
    const fullFromUser =
      user?.name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
      user?.username ||
      user?.email ||
      "";
    if (fullFromUser) return fullFromUser;

    const decoded = safeDecodeJwt(getToken());
    const fromJwt =
      decoded?.name ||
      [decoded?.first_name, decoded?.last_name].filter(Boolean).join(" ").trim() ||
      decoded?.username ||
      decoded?.email ||
      "";
    return fromJwt || "Unknown";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchTeamMembersList = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setTeamMembers([]);
      setTeamMembersDebug("No auth token");
      return;
    }

    setLoadingTeamMembers(true);
    setTeamMembersDebug("");

    try {
      const rows = await fetchTeamMembers({ apiBase: API_BASE, token });
      setTeamMembers(rows);
      setTeamMembersDebug(rows.length ? "" : "No team members found");
    } catch (err) {
      console.error("Failed to load team members:", err);
      setTeamMembers([]);
      setTeamMembersDebug(err?.message || "Failed to load team members");
    } finally {
      setLoadingTeamMembers(false);
    }
  }, []);

  const selectedTeamMember = useMemo(() => {
    const typed = normalizeTeamMemberName(assignedTeamMember);
    if (!typed) return null;

    const byName = teamMembers.find((item) => item.name.toLowerCase() === typed.toLowerCase());
    if (byName) return byName;

    return {
      id: null,
      name: typed,
    };
  }, [assignedTeamMember, teamMembers]);

  const companyNameValue = useMemo(
    () => normalizeCompanyName(companyOption?.company_name || companyInputValue || ""),
    [companyInputValue, companyOption]
  );

  const jobLinkSummary = useMemo(() => {
    const summary = describeLinkAutofill(deriveLinkAutofill(postLink, "job"), {
      includeWebsite: true,
    });
    return summary || "Autofills source and company before parsing.";
  }, [postLink]);

  const hiringManagerValue = useMemo(
    () => normalizeHiringManagerValue(hiringManagerName),
    [hiringManagerName]
  );

  const selectedCompanyId = useMemo(() => {
    const value = Number(companyOption?.company_id || 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [companyOption?.company_id]);

  const hiringManagerOptions = useMemo(
    () =>
      mergeHiringManagerOptions([
        selectedCompanyId ? companyHiringManagerOptions : defaultHiringManagerOptions,
        platformHiringManagerOptions,
        manualHiringManagerOptions,
      ]),
    [
      companyHiringManagerOptions,
      defaultHiringManagerOptions,
      manualHiringManagerOptions,
      platformHiringManagerOptions,
      selectedCompanyId,
    ]
  );

  const selectedHiringManagerOption = useMemo(() => {
    const normalizedValue = hiringManagerValue.toLowerCase();
    if (!normalizedValue) return null;

    return (
      hiringManagerOptions.find(
        (option) => normalizeHiringManagerValue(option?.name || option?.label || "").toLowerCase() === normalizedValue
      ) || null
    );
  }, [hiringManagerOptions, hiringManagerValue]);

  const loadingHiringManagerOptions = selectedCompanyId
    ? loadingCompanyHiringManagers || loadingPlatformHiringManagers
    : loadingOverviewOptions || loadingPlatformHiringManagers;

  const applySplitUrlState = useCallback(
    (url, setProtocolState, setValueState, fallbackProtocol = "http://") => {
      const parts = splitHttpUrl(url, fallbackProtocol);
      setProtocolState(parts.protocol);
      setValueState(parts.value);
    },
    []
  );

  const filterCompanyOptions = useCallback(
    (options, params) => {
      const inputValue = normalizeCompanyName(params.inputValue);
      const normalizedInput = normalizeLookupValue(inputValue);
      const filtered = options.filter((option) => {
        if (!normalizedInput) return true;
        return normalizeLookupValue(option?.company_name || "").includes(normalizedInput);
      });
      const base = companyFilter(filtered, {
        ...params,
        inputValue: "",
      });
      const exists = options.some(
        (option) =>
          normalizeLookupValue(option?.company_name || "") === normalizedInput
      );

      if (inputValue && !exists) {
        base.push({
          inputValue,
          company_id: null,
          company_name: inputValue,
          company_logo: "",
          website: "",
        });
      }

      return base;
    },
    []
  );

  const handleCompanyInputChange = useCallback(
    (_, newInputValue, reason) => {
      clearFieldError("company");
      if (reason === "reset") return;
      const normalizedInput = normalizeCompanyName(newInputValue);
      if (normalizedInput !== companyNameValue) {
        setHiringManagerName("");
        setManualHiringManagerOptions([]);
      }
      setCompanyInputValue(normalizedInput);

      if (!normalizedInput) {
        setCompanyOption(null);
        return;
      }

      const exact = companies.find(
        (option) =>
          normalizeLookupValue(option?.company_name || "") ===
          normalizeLookupValue(normalizedInput)
      );
      setCompanyOption(exact || null);

      if (exact?.website) {
        applySplitUrlState(exact.website, setWebsiteProtocol, setWebsiteUrlValue, "https://");
        setParsedCompanyProfile((prev) => ({
          ...prev,
          company_name: exact.company_name || prev.company_name,
          company_website: exact.website || prev.company_website,
        }));
      }
    },
    [applySplitUrlState, clearFieldError, companies, companyNameValue]
  );

  const handleCompanyChange = useCallback((_, newValue) => {
    clearFieldError("company");
    setHiringManagerName("");
    setManualHiringManagerOptions([]);

    if (typeof newValue === "string") {
      const normalizedInput = normalizeCompanyName(newValue);
      setCompanyInputValue(normalizedInput);
      setCompanyOption(null);
      return;
    }

    if (newValue?.inputValue) {
      const normalizedInput = normalizeCompanyName(newValue.inputValue);
      setCompanyInputValue(normalizedInput);
      setCompanyOption(null);
      return;
    }

    const nextOption = buildCompanyOption(newValue);
    setCompanyOption(nextOption);
    setCompanyInputValue(nextOption?.company_name || "");

    if (nextOption?.website) {
      applySplitUrlState(nextOption.website, setWebsiteProtocol, setWebsiteUrlValue, "https://");
      setParsedCompanyProfile((prev) => ({
        ...prev,
        company_name: nextOption.company_name || prev.company_name,
        company_website: nextOption.website || prev.company_website,
      }));
    }
  }, [applySplitUrlState, clearFieldError]);

  const handleMenuLinkChange = useCallback(
    (e) => {
      const nextValue = e?.target?.value || "";
      const autofill = deriveLinkAutofill(nextValue, "job");
      handlePostLinkFieldChange(nextValue);

      if (autofill.source) setSource(autofill.source);

      if (String(autofill.companyName || "").trim().length > 1) {
        const matchedCompany = findMatchingCompanyOption(companies, {
          companyName: autofill.companyName,
          website: autofill.website || "",
        });

        if (matchedCompany) {
          setCompanyOption(matchedCompany);
          setCompanyInputValue(matchedCompany.company_name || autofill.companyName);
        } else {
          setCompanyOption(null);
          setCompanyInputValue(autofill.companyName);
        }
      }

      setErrorMsg("");
    },
    [companies]
  );

  const filterHiringManagerOptions = useCallback(
    (options, params) => {
      const inputValue = normalizeHiringManagerValue(params.inputValue);
      const normalizedInput = normalizeLookupValue(inputValue);
      const filtered = options.filter((option) => {
        if (!normalizedInput) return true;

        const searchableText = [
          option?.name,
          option?.label,
          option?.role,
          option?.email,
          option?.phone,
        ]
          .map((value) => normalizeLookupValue(value))
          .filter(Boolean)
          .join(" ");

        return searchableText.includes(normalizedInput);
      });
      const base = hiringManagerFilter(filtered, {
        ...params,
        inputValue: "",
      });
      const exists = options.some(
        (option) =>
          normalizeHiringManagerValue(option?.name || option?.label || "").toLowerCase() ===
          inputValue.toLowerCase()
      );

      if (inputValue && !exists) {
        base.push({ inputValue, label: inputValue, name: inputValue });
      }

      return base;
    },
    []
  );

  const getHiringManagerOptionLabel = useCallback((option) => {
    if (typeof option === "string") return option;
    if (option?.inputValue) return option.inputValue;
    return String(option?.label || option?.name || "");
  }, []);

  const commitHiringManagerValue = useCallback((value) => {
    const normalizedValue = normalizeHiringManagerValue(
      typeof value === "string"
        ? value
        : value?.inputValue || value?.name || value?.label || ""
    );
    setHiringManagerName(normalizedValue);

    if (!normalizedValue) return;

    if (typeof value !== "string" && !value?.inputValue) return;

    setManualHiringManagerOptions((prev) =>
      mergeHiringManagerOptions([prev, [{ name: normalizedValue, label: normalizedValue }]])
    );
  }, []);

  const handleAssignedTeamMemberChange = useCallback((value) => {
    setAssignedTeamMember(normalizeTeamMemberName(value));
  }, []);

  // reset on open
  useEffect(() => {
    if (!open) return;
    const normalizedInitialScreen = ["menu", "link", "upload", "wizard"].includes(
      String(initialScreen || "").toLowerCase()
    )
      ? String(initialScreen || "").toLowerCase()
      : "menu";
    const parsedInitialWizardStep = Number(initialWizardStep);
    const normalizedInitialWizardStep =
      normalizedInitialScreen === "wizard" && Number.isFinite(parsedInitialWizardStep)
        ? Math.min(2, Math.max(0, parsedInitialWizardStep))
        : 0;
    const initialCompanyOption =
      buildCompanyOption(initialValues?.company || initialValues?.companyOption || {}) || null;
    const initialCompanyContactOptions = mergeHiringManagerOptions([
      Array.isArray(initialValues?.contacts) ? initialValues.contacts : [],
      Array.isArray(initialValues?.hiringManagers) ? initialValues.hiringManagers : [],
      Array.isArray(initialCompanyOption?.contacts) ? initialCompanyOption.contacts : [],
      Array.isArray(initialValues?.company?.contacts) ? initialValues.company.contacts : [],
      Array.isArray(initialValues?.company?.hiringManagers)
        ? initialValues.company.hiringManagers
        : Array.isArray(initialValues?.company?.hiring_managers)
        ? initialValues.company.hiring_managers
        : [],
      buildCompanyHiringManagerOptions(initialValues?.company || {}),
    ]);
    const initialHiringManagerOption =
      buildHiringManagerOption(
        initialValues?.hiringManager ||
          initialValues?.contact ||
          initialValues?.primaryContact ||
          {}
      ) ||
      initialCompanyContactOptions.find(
        (option) => String(option?.contact_kind || "").toLowerCase() === "hiring_manager"
      ) ||
      initialCompanyContactOptions[0] ||
      null;
    const initialCompanyWebsite = String(
      initialValues?.companyWebsite ||
        initialValues?.company_website ||
        initialValues?.company?.company_website ||
        initialValues?.company?.website ||
        initialCompanyOption?.website ||
        ""
    ).trim();
    const initialCareerWebsite = String(
      initialValues?.careerWebsite ||
        initialValues?.career_website ||
        initialValues?.companyCareerWebsite ||
        initialValues?.company_career_website ||
        initialValues?.company?.company_career_website ||
        initialValues?.company?.career_website ||
        initialValues?.company?.career_site ||
        initialCompanyOption?.career_site ||
        ""
    ).trim();
    const initialSource = String(
      initialValues?.source || initialValues?.company?.source || initialCompanyOption?.source || ""
    ).trim();
    const normalizedInitialCandidateStage = String(
      initialValues?.candidateStage ||
        initialValues?.stage ||
        initialCandidateStage ||
        normalizedCandidateStageOptions[0]?.value ||
        ""
    ).trim();
    const initialJobStatus = isCompanyQuickAction
      ? normalizeQuickActionJobStatus(
          initialValues?.jobStatus || initialValues?.job_status || "",
          "New"
        )
      : String(initialValues?.jobStatus || initialValues?.job_status || "").trim();
    setErrorMsg("");
    setFieldErrors({});
    setScreen(normalizedInitialScreen);
    setWizardStep(normalizedInitialWizardStep);

    setJobTitle(String(initialValues?.jobTitle || initialValues?.job_title || "").trim());
    setJobStatus(initialJobStatus);
    setEmployment("");
    setWorkArrangement("");
    setJobLocation(
      normalizeLocationValueForSave(
        initialValues?.location || initialValues?.company?.location || ""
      )
    );
    setHiringManagerName(initialHiringManagerOption?.name || "");
    setSlots("");
    setCandidateStage(normalizedInitialCandidateStage);
    setCompanyOption(initialCompanyOption);
    setCompanyInputValue(initialCompanyOption?.company_name || "");
    setDefaultHiringManagerOptions([]);
    setCompanyHiringManagerOptions(initialCompanyContactOptions);
    setManualHiringManagerOptions(initialHiringManagerOption ? [initialHiringManagerOption] : []);

    setPostLink("");
    setSource(initialSource);
    setUploadFile(null);

    setDescription("");
    setSkills([]);
    setSoftSkills([]);
    setSkillInput("");
    setSoftSkillInput("");
    setCompensation("");
    setResponsibilities("");
    setQualifications("");
    setEducationRequirements("");
    setExperienceRequirements("");
    setInterviewProcess([]);
    setInterviewInput("");
    setParsedCompanyProfile({
      company_name: initialCompanyOption?.company_name || "",
      company_website: initialCompanyWebsite,
      company_career_website: initialCareerWebsite,
      company_industry: String(
        initialValues?.industry ||
          initialValues?.company_industry ||
          initialValues?.company?.company_industry ||
          initialValues?.company?.industry ||
          initialCompanyOption?.industry ||
          ""
      ).trim(),
      company_size: String(
        initialValues?.company_size ||
          initialValues?.size_range ||
          initialValues?.company?.company_size ||
          initialValues?.company?.size_range ||
          initialCompanyOption?.size_range ||
          ""
      ).trim(),
      company_description: String(
        initialValues?.company_description ||
          initialValues?.full_company_description ||
          initialValues?.company?.company_description ||
          initialValues?.company?.full_company_description ||
          initialCompanyOption?.full_company_description ||
          ""
      ).trim(),
      company_logo_url: String(
        initialValues?.company_logo_url ||
          initialValues?.company?.company_logo_url ||
          initialValues?.company?.company_logo ||
          initialValues?.company?.logo ||
          initialCompanyOption?.company_logo ||
          ""
      ).trim(),
      source: initialSource,
    });

    setPostProtocol("http://");
    setPostUrlValue("");
    applySplitUrlState(initialCompanyWebsite, setWebsiteProtocol, setWebsiteUrlValue, "https://");
    setCareerWebsite(initialCareerWebsite);
    setOtherLinks([createEmptyOtherLink()]);

    setNotes(String(initialValues?.notes || "").trim());
    setAssignedTeamMember("");
    setDocuments([]);
    setTeamMembersDebug("");
    setParsingLink(false);
    if (initialCompanyOption) {
      setCompanies((prev) => upsertCompanyOption(prev, initialCompanyOption));
    }
    // We intentionally initialize from the latest props only when the dialog opens.
    // Re-running this while open would wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // keep postLink in sync with protocol + value (candidate-style url input)
  useEffect(() => {
    const v = String(postUrlValue || "").trim();
    setPostLink(v ? `${postProtocol}${v}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postProtocol, postUrlValue]);

  // ✅ load companies
  useEffect(() => {
    if (!open) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setErrorMsg("");

      try {
        const token = getToken();
        const res = await fetch(`${API_BASE}/api/companies?page=1&limit=5000&compact=1`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!res.ok) {
          throw new Error(`Failed to load companies (${res.status})`);
        }

        const data = await res.json().catch(() => ({}));
        const rows = Array.isArray(data) ? data : data.rows || [];

        const list = rows
          .map((row) => buildCompanyOption(row))
          .filter(Boolean)
          .sort((a, b) => a.company_name.localeCompare(b.company_name));

        setCompanies(list);
      } catch (err) {
        console.error("Failed to load companies:", err);
        setCompanies([]);
        setErrorMsg("Failed to load companies list.");
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const loadOverviewOptions = async () => {
      setLoadingOverviewOptions(true);

      try {
        const token = getToken();
        const res = await fetch(`${API_BASE}/api/jobs/filter-options`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!res.ok) throw new Error(`Failed to load overview options (${res.status})`);

        const data = await res.json().catch(() => ({}));
        setJobTitleOptions(
          Array.isArray(data?.job_titles) ? data.job_titles.filter(Boolean) : []
        );
        setDefaultHiringManagerOptions(
          mergeHiringManagerOptions([
            Array.isArray(data?.hiring_manager_names)
              ? data.hiring_manager_names.filter(Boolean)
              : [],
          ])
        );
      } catch (err) {
        console.error("Failed to load add-job overview options:", err);
        setJobTitleOptions([]);
        setDefaultHiringManagerOptions([]);
      } finally {
        setLoadingOverviewOptions(false);
      }
    };

    loadOverviewOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchTeamMembersList();
  }, [open, fetchTeamMembersList]);

  useEffect(() => {
    if (!open) return undefined;

    if (!selectedCompanyId) {
      setCompanyHiringManagerOptions([]);
      setLoadingCompanyHiringManagers(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    const loadCompanyHiringManagers = async () => {
      setLoadingCompanyHiringManagers(true);

      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/api/companies/${selectedCompanyId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to load company contacts (${response.status})`);
        }

        if (!active) return;
        setCompanyHiringManagerOptions(
          buildCompanyHiringManagerOptions(payload?.company || {})
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to load company-specific contacts:", err);
        if (active) {
          setCompanyHiringManagerOptions([]);
        }
      } finally {
        if (active) {
          setLoadingCompanyHiringManagers(false);
        }
      }
    };

    loadCompanyHiringManagers();

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, selectedCompanyId]);

  useEffect(() => {
    if (!open || !selectedCompanyId) return;
    if (hiringManagerValue) return;
    if (companyHiringManagerOptions.length !== 1) return;

    setHiringManagerName(companyHiringManagerOptions[0].name || "");
  }, [companyHiringManagerOptions, hiringManagerValue, open, selectedCompanyId]);

  useEffect(() => {
    const query = normalizeHiringManagerValue(hiringManagerName);
    if (!open || query.length < 2) {
      setPlatformHiringManagerOptions([]);
      setLoadingPlatformHiringManagers(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setLoadingPlatformHiringManagers(true);
      searchPlatformContacts({ apiBase: API_BASE, search: query, signal: controller.signal })
        .then((rows) => setPlatformHiringManagerOptions(mergeHiringManagerOptions(rows)))
        .catch((error) => {
          if (error?.name !== "AbortError") setPlatformHiringManagerOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingPlatformHiringManagers(false);
        });
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [hiringManagerName, open]);

  const dialogTitle =
    screen === "link" ? "Add Job - Link" : screen === "upload" ? "Add Job - Upload" : "Add Job";

  const getMissingJobRequiredFields = useCallback(
    (scope = "all") => {
      const nextErrors = {};
      const includeOverview = scope === "overview" || scope === "all" || scope === "quickCreate";
      const includeRole = scope === "role" || scope === "all";
      const selectedStageValue = String(
        candidateStage ||
          initialCandidateStage ||
          normalizedCandidateStageOptions[0]?.value ||
          ""
      ).trim();

      if (includeOverview) {
        if (!String(jobTitle || "").trim()) {
          nextErrors.jobTitle = buildRequiredFieldError("jobTitle");
        }

        if (!String(companyNameValue || "").trim()) {
          nextErrors.company = buildRequiredFieldError("company");
        }

        if (!isCompanyQuickAction && !String(hiringManagerValue || "").trim()) {
          nextErrors.hiringManager = buildRequiredFieldError("hiringManager");
        }

        if (showCandidateStageField && !selectedStageValue) {
          nextErrors.candidateStage = buildRequiredFieldError("candidateStage");
        }
      }

      if (includeRole && !String(description || "").trim()) {
        nextErrors.description = buildRequiredFieldError("description");
      }

      return nextErrors;
    },
    [
      candidateStage,
      companyNameValue,
      description,
      hiringManagerValue,
      initialCandidateStage,
      isCompanyQuickAction,
      jobTitle,
      normalizedCandidateStageOptions,
      showCandidateStageField,
    ]
  );

  const validateJobRequiredFields = useCallback(
    (scope = "all") => {
      const nextErrors = getMissingJobRequiredFields(scope);
      setScopedFieldErrors(scope, nextErrors);
      if (!Object.keys(nextErrors).length) {
        return true;
      }

      setErrorMsg("");
      return false;
    },
    [getMissingJobRequiredFields, setScopedFieldErrors]
  );

  useEffect(() => {
    setFieldErrors((prev) => {
      if (!prev || !Object.keys(prev).length) return prev;

      const next = { ...prev };
      let changed = false;
      const selectedStageValue = String(
        candidateStage ||
          initialCandidateStage ||
          normalizedCandidateStageOptions[0]?.value ||
          ""
      ).trim();

      if (prev.postLink === buildRequiredFieldError("postLink") && String(postUrlValue || "").trim()) {
        delete next.postLink;
        changed = true;
      }

      if (prev.uploadFile === buildRequiredFieldError("uploadFile") && uploadFile) {
        delete next.uploadFile;
        changed = true;
      }

      if (prev.jobTitle === buildRequiredFieldError("jobTitle") && String(jobTitle || "").trim()) {
        delete next.jobTitle;
        changed = true;
      }

      if (prev.company === buildRequiredFieldError("company") && String(companyNameValue || "").trim()) {
        delete next.company;
        changed = true;
      }

      if (
        prev.hiringManager === buildRequiredFieldError("hiringManager") &&
        String(hiringManagerValue || "").trim()
      ) {
        delete next.hiringManager;
        changed = true;
      }

      if (
        prev.candidateStage === buildRequiredFieldError("candidateStage") &&
        (!showCandidateStageField || selectedStageValue)
      ) {
        delete next.candidateStage;
        changed = true;
      }

      if (
        prev.description === buildRequiredFieldError("description") &&
        String(description || "").trim()
      ) {
        delete next.description;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    candidateStage,
    description,
    hiringManagerValue,
    initialCandidateStage,
    jobTitle,
    normalizedCandidateStageOptions,
    postUrlValue,
    companyNameValue,
    showCandidateStageField,
    uploadFile,
  ]);

  const handlePostLinkFieldChange = (value) => {
    const nextValue = String(value || "").trim();
    if (!nextValue) {
      setPostProtocol("http://");
      setPostUrlValue("");
      return;
    }

    if (/^https?:\/\//i.test(nextValue)) {
      const nextProtocol = nextValue.toLowerCase().startsWith("https://")
        ? "https://"
        : "http://";
      setPostProtocol(nextProtocol);
      setPostUrlValue(nextValue.replace(/^https?:\/\//i, ""));
      return;
    }

    setPostUrlValue(nextValue);
  };

  useEffect(() => {
    if (!open) return;

    const selectedCompanyWebsite = String(companyOption?.website || "").trim();
    if (!selectedCompanyWebsite) return;

    const currentWebsite = String(buildHttpUrl(websiteProtocol, websiteUrlValue) || "").trim();
    if (currentWebsite) return;

    applySplitUrlState(
      selectedCompanyWebsite,
      setWebsiteProtocol,
      setWebsiteUrlValue,
      "https://"
    );
    setParsedCompanyProfile((prev) => ({
      ...prev,
      company_name: prev.company_name || companyOption?.company_name || "",
      company_website: prev.company_website || selectedCompanyWebsite,
    }));
  }, [
    applySplitUrlState,
    companyOption?.company_name,
    companyOption?.website,
    open,
    websiteProtocol,
    websiteUrlValue,
  ]);

  const createCompanyIfMissing = useCallback(
    async (token, companyName, companyProfile = {}) => {
      const normalizedName = normalizeCompanyName(companyName);
      if (!normalizedName) return null;

      const existing = companies.find(
        (option) =>
          normalizeLookupValue(option?.company_name || "") ===
          normalizeLookupValue(normalizedName)
      );
      if (existing) return existing;

      const response = await fetch(`${API_BASE}/api/companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: normalizedName,
          website: String(
            companyProfile?.company_website || companyProfile?.website || ""
          ).trim(),
          career_site: String(
            companyProfile?.company_career_website || companyProfile?.career_site || ""
          ).trim(),
          location: String(companyProfile?.location || "").trim(),
          industry: String(companyProfile?.company_industry || companyProfile?.industry || "").trim(),
          size_range: String(companyProfile?.company_size || companyProfile?.size_range || "").trim(),
          full_company_description: String(
            companyProfile?.company_description ||
              companyProfile?.full_company_description ||
              ""
          ).trim(),
          logo: String(companyProfile?.company_logo_url || companyProfile?.logo || "").trim(),
          source:
            String(companyProfile?.source || source || "").trim() || "Manual Entry",
          status: "Active",
          ...(companyProfile?.interactive_import || companyProfile?.interactiveImport
            ? { interactive_import: true }
            : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Failed to create company.");
      }

      const nextOption =
        buildCompanyOption(data?.company) ||
        buildCompanyOption({
          company_id: data?.companyId ?? data?.id,
          company_name: data?.name ?? normalizedName,
          company_logo: data?.logo ?? "",
          website: data?.website ?? "",
        });

      if (!nextOption?.company_name) {
        throw new Error("Company was created, but the response was incomplete.");
      }

      setCompanies((prev) => upsertCompanyOption(prev, nextOption));
      return nextOption;
    },
    [companies, source]
  );

  const ensureCompanyOption = useCallback(async () => {
    const normalizedName = normalizeCompanyName(
      companyOption?.company_name || companyInputValue || ""
    );
    if (!normalizedName) return null;

    const resolvedCompanyProfile = {
      ...parsedCompanyProfile,
      company_name: normalizedName,
      company_website:
        buildHttpUrl(websiteProtocol, websiteUrlValue) ||
        parsedCompanyProfile.company_website ||
        companyOption?.website ||
        "",
      company_career_website:
        String(careerWebsite || parsedCompanyProfile.company_career_website || "").trim(),
      source: String(source || parsedCompanyProfile.source || "").trim(),
      ...(parsedCompanyProfile &&
      Object.values(parsedCompanyProfile).some((value) => String(value || "").trim())
        ? { interactive_import: true }
        : {}),
    };

    if (companyOption?.company_id) {
      const normalizedOption = buildCompanyOption(companyOption) || companyOption;
      setCompanyOption(normalizedOption);
      setCompanyInputValue(normalizedOption?.company_name || normalizedName);
      return normalizedOption;
    }

    const existing = companies.find(
      (option) =>
        normalizeLookupValue(option?.company_name || "") ===
        normalizeLookupValue(normalizedName)
    );
    if (existing) {
      setCompanyOption(existing);
      setCompanyInputValue(existing.company_name || normalizedName);
      return existing;
    }

    const token = getToken();
    if (!token) {
      throw new Error("No auth token found. Please login again.");
    }

    setCreatingCompany(true);
    try {
      const created = await createCompanyIfMissing(
        token,
        normalizedName,
        resolvedCompanyProfile
      );
      if (created) {
        setCompanyOption(created);
        setCompanyInputValue(created.company_name || normalizedName);
      }
      return created;
    } finally {
      setCreatingCompany(false);
    }
  }, [
    careerWebsite,
    companies,
    companyInputValue,
    companyOption,
    createCompanyIfMissing,
    parsedCompanyProfile,
    source,
    websiteProtocol,
    websiteUrlValue,
  ]);

  const navigateToCreatedJob = useCallback(
    (job) => {
      // Some entry points (such as a company profile's Add Job quick action) need to
      // remain in their current context after saving so they can display the updated
      // company jobs list instead of opening the individual job detail page.
      if (variant === "companyQuickAction") {
        const companyId = job?.company_id ?? job?.companyId ?? companyOption?.company_id;
        if (!companyId) return;

        const search = new URLSearchParams({
          companyId: String(companyId),
          tab: "jobs",
          entityType: "company",
          returnTab: "companies",
        }).toString();

        navigate({ pathname: "/companies", search: `?${search}` });
        return;
      }

      const jobId = job?.id ?? job?.job_id ?? null;
      if (!jobId) return;

      const search = new URLSearchParams({
        jobId: String(jobId),
        tab: "details",
      }).toString();

      navigate(
        {
          pathname: "/jobs",
          search: `?${search}`,
        },
        {
          replace: false,
          state: {
            createdJobId: String(jobId),
            createdJob: job,
            source: "add-job-dialog",
          },
        }
      );
    },
    [companyOption?.company_id, navigate, variant]
  );

  const createJobFromPayload = useCallback(
    async ({ payload, file }) => {
      const token = getToken();
      if (!token) {
        throw new Error("No auth token found. Please login again.");
      }

      const fd = new FormData();
      fd.append("data", JSON.stringify(payload));
      if (file) fd.append("job_file", file);

      const res = await fetch(`${API_BASE}/api/jobs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && data?.duplicate && data?.job) {
          const existingJob = data.job;
          setErrorMsg("Job already exists; redirecting to existing job.");
          await onCreated?.(existingJob);
          notifyJobCreated(existingJob);
          navigateToCreatedJob(existingJob);
          onClose?.();
          return { duplicate: true, job: existingJob };
        }
        throw new Error(data?.error || res.statusText || "Failed to create job.");
      }

      const createdJob = data?.job || data;
      await onCreated?.(createdJob);
      notifyJobCreated(createdJob);
      navigateToCreatedJob(createdJob);
      onClose?.();
      return { duplicate: false, job: createdJob };
    },
    [navigateToCreatedJob, notifyJobCreated, onClose, onCreated]
  );

  const ensureCompanyForParsedJob = useCallback(
    async (token, parsed = {}) => {
      if (companyOption?.company_id) return companyOption;

      const companyName = String(
        parsed.company_name || parsed.company || companyInputValue || ""
      ).trim();
      if (!companyName) {
        throw new Error("Company name could not be detected from the uploaded file.");
      }

      const existing = companies.find(
        (option) =>
          normalizeLookupValue(option?.company_name || "") ===
          normalizeLookupValue(companyName)
      );
      if (existing) return existing;

      const companyProfile = {
        company_website: String(parsed.company_website || parsed.website || "").trim(),
        company_career_website: String(
          parsed.career_website || parsed.company_career_website || ""
        ).trim(),
        company_industry: String(parsed.company_industry || parsed.industry || "").trim(),
        company_size: String(parsed.company_size || parsed.size_range || "").trim(),
        company_description: String(
          parsed.company_description || parsed.full_company_description || ""
        ).trim(),
        company_logo_url: String(parsed.company_logo_url || parsed.company_logo || "").trim(),
        source: String(parsed.source || source || "").trim(),
        interactive_import: true,
      };

      return await createCompanyIfMissing(token, companyName, companyProfile);
    },
    [companyInputValue, companyOption, companies, createCompanyIfMissing, source]
  );

  const handleUploadFileCreate = useCallback(async (fileArg = null) => {
    setErrorMsg("");
    const selectedFile = fileArg || uploadFile;
    if (!selectedFile) {
      setScopedFieldErrors("upload", {
        uploadFile: buildRequiredFieldError("uploadFile"),
      });
      return;
    }

    setScopedFieldErrors("upload", {});

    if (fileArg && !uploadFile) {
      setUploadFile(selectedFile);
    }

    const token = getToken();
    if (!token) {
      setErrorMsg("No auth token found. Please login again.");
      return;
    }

    setProcessingUpload(true);
    try {
      const fd = new FormData();
      fd.append("job_file", selectedFile);
      if (companyOption?.company_id) {
        fd.append("company_id", String(companyOption.company_id));
      } else if (companyInputValue) {
        fd.append("company_name", companyInputValue.trim());
      }
      if (String(source || "").trim()) {
        fd.append("source", String(source).trim());
      }

      const res = await fetch(`${API_BASE}/api/jobs/parse-file`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || res.statusText || "Failed to parse the uploaded file.");
      }

      const parsed = data?.parsed || {};
      const jobTitleValue = String(parsed.job_title || parsed.title || "").trim();
      const companyNameValue = String(parsed.company_name || parsed.company || "").trim();

      if (!jobTitleValue || !companyNameValue) {
        setJobTitle(jobTitleValue);
        setCompanyInputValue(companyNameValue || companyInputValue);
        setParsedCompanyProfile((prev) => ({
          ...prev,
          company_name: parsed.company_name || parsed.company || prev.company_name,
          company_website: parsed.company_website || parsed.website || prev.company_website,
          company_career_website:
            parsed.career_website || parsed.company_career_website || prev.company_career_website,
          company_industry: parsed.company_industry || parsed.industry || prev.company_industry,
          company_size: parsed.company_size || parsed.size_range || prev.company_size,
          company_description:
            parsed.company_description || parsed.full_company_description || prev.company_description,
          company_logo_url: parsed.company_logo_url || parsed.company_logo || prev.company_logo_url,
          source: parsed.source || source || prev.source,
        }));
        setErrorMsg(
          "The file was uploaded, but we could not detect a complete job title or company. Please review the form manually."
        );
        setScreen("wizard");
        setWizardStep(0);
        return;
      }

      setJobTitle(jobTitleValue);
      setCompanyInputValue(companyNameValue);
      setSource(String(parsed.source || source || "").trim());
      setWorkArrangement(String(parsed.work_arrangement || "").trim());
      setJobLocation(normalizeLocationValueForSave(parsed.location || ""));
      setHiringManagerName(String(parsed.hiring_manager_name || parsed.contact_name || "").trim());
      setSlots(parsed.job_openings ? String(parsed.job_openings) : "");
      setDescription(String(parsed.description || "").trim());
      setResponsibilities(String(parsed.responsibilities || "").trim());
      setQualifications(String(parsed.qualifications || "").trim());
      setEducationRequirements(String(parsed.education_requirements || parsed.education || "").trim());
      setExperienceRequirements(String(parsed.experience_requirements || parsed.experience || "").trim());
      setCompensation(String(parsed.compensation || parsed.salary_range || "").trim());
      setSkills(normalizeUniqueStrings(parsed.skills || []));
      setSoftSkills(normalizeUniqueStrings(parsed.soft_skills || []));
      setInterviewProcess(Array.isArray(parsed.interview_process) ? parsed.interview_process : []);
      setWebsiteProtocol("https://");
      setWebsiteUrlValue(String(parsed.website || parsed.company_website || "").replace(/^https?:\/\//i, ""));
      setCareerWebsite(String(parsed.career_website || parsed.company_career_website || parsed.post_link || "").trim());
      setParsedCompanyProfile({
        company_name: String(parsed.company_name || parsed.company || "").trim(),
        company_website: String(parsed.company_website || parsed.website || "").trim(),
        company_career_website: String(parsed.career_website || parsed.company_career_website || "").trim(),
        company_industry: String(parsed.company_industry || parsed.industry || "").trim(),
        company_size: String(parsed.company_size || parsed.size_range || "").trim(),
        company_description: String(parsed.company_description || parsed.full_company_description || "").trim(),
        company_logo_url: String(parsed.company_logo_url || parsed.company_logo || "").trim(),
        source: String(parsed.source || source || "").trim(),
      });

      const company = await ensureCompanyForParsedJob(token, parsed);
      const payload = {
        job_title: jobTitleValue,
        company_id: Number(company.company_id),
        company_name: company.company_name,
        employment: normalizeEmploymentType(parsed.employment || parsed.employment_type || "") || null,
        work_arrangement: String(parsed.work_arrangement || "").trim() || null,
        location: normalizeLocationValueForSave(parsed.location || ""),
        source: String(parsed.source || source || "").trim(),
        post_link: String(parsed.post_link || parsed.job_url || "").trim() || "",
        hiring_manager_name: String(parsed.hiring_manager_name || parsed.contact_name || "").trim() || "",
        description: String(parsed.description || "").trim() || "",
        responsibilities: String(parsed.responsibilities || "").trim() || "",
        qualifications: String(parsed.qualifications || "").trim() || "",
        education_requirements: String(parsed.education_requirements || parsed.education || "").trim() || "",
        experience_requirements: String(parsed.experience_requirements || parsed.experience || "").trim() || "",
        skills: normalizeUniqueStrings(parsed.skills || []),
        soft_skills: normalizeUniqueStrings(parsed.soft_skills || []),
        compensation: String(parsed.compensation || parsed.salary_range || "").trim() || "",
        interview_process: Array.isArray(parsed.interview_process) ? parsed.interview_process : [],
        website: String(parsed.website || parsed.company_website || "").trim() || "",
        company_website: String(parsed.company_website || parsed.website || "").trim() || "",
        career_website: String(parsed.career_website || parsed.company_career_website || parsed.post_link || "").trim() || "",
        company_career_website: String(parsed.career_website || parsed.company_career_website || parsed.post_link || "").trim() || "",
        company_industry: String(parsed.company_industry || parsed.industry || "").trim() || "",
        company_size: String(parsed.company_size || parsed.size_range || "").trim() || "",
        company_description: String(parsed.company_description || parsed.full_company_description || "").trim() || "",
        company_logo_url: String(parsed.company_logo_url || parsed.company_logo || "").trim() || "",
        other_links: Array.isArray(parsed.other_links) ? parsed.other_links : [],
      };

      await createJobFromPayload({ payload, file: selectedFile });
    } catch (err) {
      console.error("Upload file job creation failed:", err);
      setErrorMsg(err?.message || "Failed to process upload file.");
    } finally {
      setProcessingUpload(false);
    }
  }, [companyInputValue, companyOption, createJobFromPayload, ensureCompanyForParsedJob, setScopedFieldErrors, source, uploadFile]);

  const handleParseJobLink = async () => {
    const link = String(postLink || "").trim();
    if (!link) {
      setScopedFieldErrors("link", {
        postLink: buildRequiredFieldError("postLink"),
      });
      setErrorMsg("");
      return;
    }

    setScopedFieldErrors("link", {});

    const token = getToken();
    if (!token) {
      setErrorMsg("No auth token found. Please login again.");
      return;
    }

    setErrorMsg("");
    setParsingLink(true);

    try {
      const linkAutofill = deriveLinkAutofill(link, "job");
      const hintedSource = String(source || linkAutofill.source || "").trim();
      const hintedCompanyName = String(
        companyOption?.company_name || companyInputValue || linkAutofill.companyName || ""
      ).trim();

      const response = await fetch(`${API_BASE}/api/jobs/parse-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: link,
          source: hintedSource,
          company_id: companyOption?.company_id || null,
          company_name: hintedCompanyName,
          company_website: companyOption?.website || linkAutofill.website || "",
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || response.statusText || "Failed to parse job link");
      }

      const parsed = data?.parsed || {};
      const matchedCompany =
        companyOption?.company_id
          ? companyOption
          : findMatchingCompanyOption(companies, {
              companyName: parsed.company_name || hintedCompanyName,
              website: parsed.website || linkAutofill.website || "",
            });
      const nextPostLink = String(parsed.post_link || link).trim();
      const nextSource = String(parsed.source || hintedSource || "").trim();
      const nextCompanyName = String(
        parsed.company_name || hintedCompanyName || matchedCompany?.company_name || ""
      ).trim();
      const nextWebsite = String(
        parsed.website || matchedCompany?.website || companyOption?.website || linkAutofill.website || ""
      ).trim();
      const nextCareerWebsite = String(
        parsed.career_website || nextPostLink || ""
      ).trim();
      const nextCompanyIndustry = String(parsed.company_industry || "").trim();
      const nextCompanySize = String(parsed.company_size || "").trim();
      const nextCompanyDescription = String(parsed.company_description || "").trim();
      const nextCompanyLogoUrl = String(parsed.company_logo_url || "").trim();
      const parsedSkills = normalizeUniqueStrings(parsed.skills || []);
      const parsedSoftSkills = normalizeUniqueStrings(parsed.soft_skills || []);
      const parsedInterviewProcess = normalizeUniqueStrings(parsed.interview_process || []);
      const parsedLinks = Array.isArray(parsed.other_links)
        ? parsed.other_links
            .map((item) => toLinkState(item?.url || "", item?.label || ""))
            .filter((item) => item.value)
        : [];

      const nextJobTitle = String(parsed.job_title || "").trim();
      const nextEmployment = normalizeEmploymentType(
        parsed.employment || parsed.employment_type || ""
      );
      const nextWorkArrangement = String(parsed.work_arrangement || "").trim();
      const nextLocation = normalizeLocationValueForSave(parsed.location || "");
      const nextHiringManagerName = String(parsed.hiring_manager_name || "").trim();
      const nextSlots =
        parsed.job_openings != null && String(parsed.job_openings).trim()
          ? String(parsed.job_openings).trim()
          : "";
      const nextDescription = String(parsed.description || "").trim();
      const nextResponsibilities = String(parsed.responsibilities || "").trim();
      const nextQualifications = String(parsed.qualifications || "").trim();
      const nextEducationRequirements = String(parsed.education_requirements || "").trim();
      const nextExperienceRequirements = String(parsed.experience_requirements || "").trim();
      const nextCompensation = String(parsed.compensation || "").trim();

      const normalizedCompanyName = normalizeCompanyName(
        companyOption?.company_name || companyInputValue || nextCompanyName
      );
      const canAutoCreate = Boolean(nextJobTitle && normalizedCompanyName && nextPostLink);

      handlePostLinkFieldChange(nextPostLink);
      setJobTitle(nextJobTitle);
      setEmployment(nextEmployment);
      setWorkArrangement(nextWorkArrangement);
      setJobLocation(nextLocation);
      setHiringManagerName(nextHiringManagerName);
      setSlots(nextSlots);
      setDescription(nextDescription);
      setResponsibilities(nextResponsibilities);
      setQualifications(nextQualifications);
      setEducationRequirements(nextEducationRequirements);
      setExperienceRequirements(nextExperienceRequirements);
      setSkills(parsedSkills);
      setSoftSkills(parsedSoftSkills);
      setSkillInput("");
      setSoftSkillInput("");
      setCompensation(nextCompensation);
      setInterviewProcess(parsedInterviewProcess);
      setInterviewInput("");
      applySplitUrlState(nextWebsite, setWebsiteProtocol, setWebsiteUrlValue, "https://");
      setCareerWebsite(nextCareerWebsite);
      setOtherLinks(parsedLinks.length ? parsedLinks : [createEmptyOtherLink()]);
      setSource(nextSource);
      setParsedCompanyProfile({
        company_name: nextCompanyName,
        company_website: nextWebsite,
        company_career_website: nextCareerWebsite,
        company_industry: nextCompanyIndustry,
        company_size: nextCompanySize,
        company_description: nextCompanyDescription,
        company_logo_url: nextCompanyLogoUrl,
        source: nextSource,
      });

      if (!companyOption?.company_id) {
        if (matchedCompany) {
          const enrichedCompany =
            buildCompanyOption({
              ...matchedCompany,
              company_name: nextCompanyName || matchedCompany.company_name,
              company_website: nextWebsite || matchedCompany.website,
              company_logo: nextCompanyLogoUrl || matchedCompany.company_logo,
              company_industry: nextCompanyIndustry || matchedCompany.industry,
              company_size: nextCompanySize || matchedCompany.size_range,
              company_career_website: nextCareerWebsite || matchedCompany.career_site,
              company_description:
                nextCompanyDescription || matchedCompany.full_company_description,
            }) || matchedCompany;
          setCompanyOption(enrichedCompany);
          setCompanyInputValue(enrichedCompany.company_name || "");
          setCompanies((prev) => upsertCompanyOption(prev, enrichedCompany));
        } else {
          setCompanyInputValue(nextCompanyName);
        }
      }

      if (canAutoCreate) {
        setSaving(true);
        try {
          const companyProfile = {
            company_name: normalizedCompanyName,
            company_website: nextWebsite,
            company_career_website: nextCareerWebsite,
            company_industry: nextCompanyIndustry,
            company_size: nextCompanySize,
            company_description: nextCompanyDescription,
            company_logo_url: nextCompanyLogoUrl,
            source: nextSource,
            interactive_import: true,
          };

          let resolvedCompany = companyOption;
          if (!resolvedCompany?.company_id) {
            const existingCompany = companies.find(
              (option) =>
                normalizeLookupValue(option?.company_name || "") ===
                normalizeLookupValue(normalizedCompanyName)
            );
            if (existingCompany) {
              resolvedCompany = existingCompany;
            }
          }

          if (!resolvedCompany?.company_id) {
            resolvedCompany = await createCompanyIfMissing(
              token,
              normalizedCompanyName,
              companyProfile
            );
            if (!resolvedCompany?.company_id) {
              throw new Error("Please select or add a valid Company (Client).");
            }
            setCompanies((prev) => upsertCompanyOption(prev, resolvedCompany));
          }

          const normalizedTechStack = Array.from(
            new Set(
              [...parsedSkills, String(skillInput || "").trim()]
                .map((value) => String(value || "").trim())
                .filter(Boolean)
            )
          );
          const normalizedSoftSkills = normalizeUniqueStrings(parsedSoftSkills);

          const normalizedOtherLinks = parsedLinks
            .map((linkItem) => {
              const url = buildHttpUrl(linkItem?.protocol, linkItem?.value);
              if (!url) return null;
              return linkItem?.label ? { label: String(linkItem.label).trim(), url } : { url };
            })
            .filter(Boolean);

          const payload = {
            job_title: nextJobTitle,
            company_id: Number(resolvedCompany.company_id),
            company_name: resolvedCompany.company_name || normalizedCompanyName,
            employment: nextEmployment || null,
            work_arrangement: nextWorkArrangement || null,
            location: nextLocation || "",
            owner: ownerName || "",
            source: nextSource || "",
            post_link: nextPostLink || "",
            hiring_manager_name: nextHiringManagerName || "",
            description: nextDescription || "",
            responsibilities: nextResponsibilities || "",
            qualifications: nextQualifications || "",
            education_requirements: nextEducationRequirements || "",
            experience_requirements: nextExperienceRequirements || "",
            skills: normalizedTechStack,
            tech_stack: normalizedTechStack.length ? normalizedTechStack.join(", ") : "",
            soft_skills: normalizedSoftSkills,
            compensation: nextCompensation || "",
            interview_process: parsedInterviewProcess.length ? parsedInterviewProcess : [],
            website: nextWebsite || "",
            company_website: nextWebsite || "",
            career_website: nextCareerWebsite || "",
            company_career_website: nextCareerWebsite || "",
            company_industry: nextCompanyIndustry || "",
            company_size: nextCompanySize || "",
            company_description: nextCompanyDescription || "",
            company_logo_url: nextCompanyLogoUrl || "",
            other_links: normalizedOtherLinks,
            notes: "",
            assigned_team_member: "",
            documents: [],
          };

          const createResponse = await fetch(`${API_BASE}/api/jobs`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          const createData = await createResponse.json().catch(() => ({}));
          if (!createResponse.ok) {
            if (createResponse.status === 409 && createData?.duplicate && createData?.job) {
              const existingJob = createData.job;
              await onCreated?.(existingJob);
              notifyJobCreated(existingJob);
              navigateToCreatedJob(existingJob);
              onClose?.();
              return;
            }
            throw new Error(createData?.error || createResponse.statusText || "Failed to create job.");
          }

          const createdJob = createData?.job || createData;
          await onCreated?.(createdJob);
          notifyJobCreated(createdJob);
          navigateToCreatedJob(createdJob);
          onClose?.();
          return;
        } catch (createError) {
          console.error("Create job from link failed:", createError);
          setErrorMsg(createError?.message || "Failed to create job.");
          return;
        } finally {
          setSaving(false);
        }
      }

      goWizard(0);
    } catch (err) {
      console.error("Parse job link failed:", err);
      setErrorMsg(err?.message || "Failed to parse job link.");
    } finally {
      setParsingLink(false);
    }
  };

  // ---- Step 1 helpers ----
  const handleAddSkill = () => {
    const v = String(skillInput || "").trim();
    if (!v) return;
    if (skills.some((s) => String(s).toLowerCase() === v.toLowerCase())) {
      setSkillInput("");
      return;
    }
    setSkills((prev) => [...prev, v]);
    setSkillInput("");
  };

  const handleRemoveSkill = (label) => {
    setSkills((prev) => prev.filter((s) => s !== label));
  };

  const handleAddSoftSkill = () => {
    const v = String(softSkillInput || "").trim();
    if (!v) return;
    if (softSkills.some((s) => String(s).toLowerCase() === v.toLowerCase())) {
      setSoftSkillInput("");
      return;
    }
    setSoftSkills((prev) => [...prev, v]);
    setSoftSkillInput("");
  };

  const handleRemoveSoftSkill = (label) => {
    setSoftSkills((prev) => prev.filter((s) => s !== label));
  };

  const handleAddInterviewStep = () => {
    const v = String(interviewInput || "").trim();
    if (!v) return;
    if (interviewProcess.some((step) => String(step).toLowerCase() === v.toLowerCase())) {
      setInterviewInput("");
      return;
    }
    setInterviewProcess((prev) => [...prev, v]);
    setInterviewInput("");
  };

  const handleRemoveInterviewStep = (label) => {
    setInterviewProcess((prev) => prev.filter((step) => step !== label));
  };

  const handleAddOtherLink = () => {
    setOtherLinks((prev) => [...prev, createEmptyOtherLink()]);
  };

  const handleUpdateOtherLink = (index, field, nextValue) => {
    setOtherLinks((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (field === "url") {
          const parts = splitHttpUrl(nextValue, item?.protocol || "http://");
          return {
            ...item,
            protocol: parts.protocol,
            value: parts.value,
          };
        }
        return {
          ...item,
          [field]: String(nextValue || ""),
        };
      })
    );
  };

  const handleRemoveOtherLink = (index) => {
    setOtherLinks((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return next.length ? next : [createEmptyOtherLink()];
    });
  };

  // ---- Step 2 helpers ----
  const addDocumentFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setDocuments((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name, file: f, addedAt: new Date().toISOString() })),
    ]);
  };

  const goWizard = (step = 0) => {
    setScreen("wizard");
    setWizardStep(step);
    setErrorMsg("");
    setFieldErrors({});
  };

  const handleNext = async () => {
    setErrorMsg("");
    if (wizardStep === 0) {
      if (!validateJobRequiredFields("overview")) return;
      try {
        const ensuredCompany = await ensureCompanyOption();
        if (!ensuredCompany?.company_id) {
          setScopedFieldErrors("overview", {
            company: "Please select or add a valid Company (Client).",
          });
          return;
        }
      } catch (err) {
        console.error("Ensure company failed:", err);
        setErrorMsg(err?.message || "Failed to create company.");
        return;
      }
      setWizardStep(1);
      return;
    }
    if (wizardStep === 1) {
      if (!validateJobRequiredFields("role")) return;
      setWizardStep(2);
      return;
    }
  };

  const handleBack = () => {
    setErrorMsg("");
    setFieldErrors({});
    if (screen === "wizard") {
      if (wizardStep === 0) {
        setScreen("menu");
        return;
      }
      setWizardStep((s) => Math.max(0, s - 1));
      return;
    }
    setScreen("menu");
  };

  const handleCreateJob = async () => {
    setErrorMsg("");
    if (!validateJobRequiredFields("all")) return;

    const token = getToken();
    if (!token) {
      setErrorMsg("No auth token found. Please login again.");
      return;
    }

    setSaving(true);
    try {
      const selectedCandidateStage = String(
        candidateStage || initialCandidateStage || normalizedCandidateStageOptions[0]?.value || ""
      ).trim();
      const ensuredCompany = await ensureCompanyOption();
      if (!ensuredCompany?.company_id) {
        setScopedFieldErrors("overview", {
          company: "Please select or add a valid Company (Client).",
        });
        return;
      }

      const normalizedTechStack = Array.from(
        new Set(
          [...skills, String(skillInput || "").trim()]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );
      const normalizedSoftSkills = normalizeUniqueStrings(softSkills);

      let finalAssignedTeamMember = normalizeTeamMemberName(assignedTeamMember);
      if (finalAssignedTeamMember) {
        const ensuredTeamMember = await createTeamMemberIfMissing({
          apiBase: API_BASE,
          token,
          name: finalAssignedTeamMember,
          existing: teamMembers,
        });
        finalAssignedTeamMember = ensuredTeamMember?.name || finalAssignedTeamMember;
        handleAssignedTeamMemberChange(finalAssignedTeamMember);
        setTeamMembers((prev) =>
          upsertTeamMemberOption(
            prev,
            ensuredTeamMember || { name: finalAssignedTeamMember }
          )
        );
      }

      const normalizedOtherLinks = otherLinks
        .map((linkItem) => {
          const url = buildHttpUrl(linkItem?.protocol, linkItem?.value);
          if (!url) return null;
          return {
            ...(String(linkItem?.label || "").trim()
              ? { label: String(linkItem.label).trim() }
              : {}),
            url,
          };
        })
        .filter(Boolean);
      const resolvedPrimaryContactRole = String(selectedHiringManagerOption?.role || "").trim();
      const companyWebsiteUrl = buildHttpUrl(websiteProtocol, websiteUrlValue);
      const resolvedCompanyName =
        ensuredCompany?.company_name ||
        String(parsedCompanyProfile.company_name || companyInputValue || "").trim();
      const resolvedCareerWebsite = String(
        careerWebsite || parsedCompanyProfile.company_career_website || ""
      ).trim();
      const resolvedCompanyWebsite = String(
        companyWebsiteUrl || parsedCompanyProfile.company_website || ensuredCompany?.website || ""
      ).trim();

      if (
        resolvedCareerWebsite &&
        resolvedCareerWebsite !== String(postLink || "").trim() &&
        resolvedCareerWebsite !== String(companyWebsiteUrl).trim() &&
        !normalizedOtherLinks.some(
          (item) => String(item?.url || "").trim() === resolvedCareerWebsite
        )
      ) {
        normalizedOtherLinks.unshift({ label: "Career Website", url: resolvedCareerWebsite });
      }

      const payload = {
        job_title: jobTitle.trim(),
        company_id: Number(ensuredCompany.company_id),
        company_name: resolvedCompanyName,
        ...(String(jobStatus || "").trim()
          ? { status: String(jobStatus || "").trim() }
          : {}),

        employment: normalizeEmploymentType(employment) || null,
        work_arrangement: workArrangement ? String(workArrangement).trim() : null,
        location: normalizeLocationValueForSave(jobLocation),
        job_openings: slots && !Number.isNaN(Number(slots)) ? Number(slots) : null,

        owner: ownerName || "",

        source: source ? String(source).trim() : "",
        post_link: postLink ? String(postLink).trim() : "",

        hiring_manager_name: hiringManagerName ? String(hiringManagerName).trim() : "",
        title_field: resolvedPrimaryContactRole,
        contact_title: resolvedPrimaryContactRole,
        hiring_manager_title: resolvedPrimaryContactRole,
        hiring_manager_role: resolvedPrimaryContactRole,

        // kept (even if not shown in Step 1 anymore)
        description: description ? String(description).trim() : "",
        responsibilities: responsibilities ? String(responsibilities).trim() : "",
        qualifications: qualifications ? String(qualifications).trim() : "",
        education_requirements: educationRequirements
          ? String(educationRequirements).trim()
          : "",
        experience_requirements: experienceRequirements
          ? String(experienceRequirements).trim()
          : "",
        skills: normalizedTechStack,
        tech_stack: normalizedTechStack.length ? normalizedTechStack.join(", ") : "",
        soft_skills: normalizedSoftSkills,
        compensation: compensation ? String(compensation).trim() : "",
        interview_process: interviewProcess.length ? interviewProcess : [],

        // extra candidate-style fields (optional)
        website: resolvedCompanyWebsite,
        company_website: resolvedCompanyWebsite,
        career_website: resolvedCareerWebsite,
        company_career_website: resolvedCareerWebsite,
        company_industry: String(parsedCompanyProfile.company_industry || "").trim(),
        company_size: String(parsedCompanyProfile.company_size || "").trim(),
        company_description: String(parsedCompanyProfile.company_description || "").trim(),
        company_logo_url: String(parsedCompanyProfile.company_logo_url || "").trim(),
        other_links: normalizedOtherLinks,

        notes: notes ? String(notes).trim() : "",
        assigned_team_member: finalAssignedTeamMember,

        documents: documents.map((d) => ({ name: d.name, addedAt: d.addedAt })),
      };

      const hasAnyFile = Boolean(uploadFile) || documents.some((d) => d.file);

      let res;
      if (hasAnyFile) {
        const fd = new FormData();
        fd.append("data", JSON.stringify(payload));
        if (uploadFile) fd.append("job_file", uploadFile);
        documents.forEach((d) => d.file && fd.append("documents", d.file));

        res = await fetch(`${API_BASE}/api/jobs`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } else {
        res = await fetch(`${API_BASE}/api/jobs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && data?.duplicate && data?.job) {
          const existingJob = data.job;
          await onCreated?.(
            existingJob,
            showCandidateStageField && selectedCandidateStage
              ? { candidateStage: selectedCandidateStage }
              : {}
          );
          notifyJobCreated(existingJob);
          if (openJobOnComplete) {
            navigateToCreatedJob(existingJob);
          }
          onClose?.();
          return;
        }
        throw new Error(data?.error || res.statusText);
      }

      const createdJob = data?.job || data;
      await onCreated?.(
        createdJob,
        showCandidateStageField && selectedCandidateStage
          ? { candidateStage: selectedCandidateStage }
          : {}
      );
      notifyJobCreated(createdJob);

      try {
        localStorage.removeItem("addJobDraft");
      } catch {
        // ignore
      }

      if (openJobOnComplete) {
        navigateToCreatedJob(createdJob);
      }
      onClose?.();
    } catch (err) {
      console.error("Create job failed:", err);
      setErrorMsg(err?.message || "Failed to create job.");
    } finally {
      setSaving(false);
    }
  };

  const renderMenu = () => (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Typography variant="body2" sx={{ color: "#6b7280" }}>
        Choose how you want to add this job.
      </Typography>

      <CardAction icon={<AddIcon fontSize="small" />} title="Start from Scratch" subtitle="Manually create a new job" onClick={() => goWizard(0)} />

      <InlineLinkEntryCard
        title="Use Link"
        subtitle="Paste a job URL and keep the parser autofill"
        value={postLink}
        onChange={handleMenuLinkChange}
        onSubmit={handleParseJobLink}
        placeholder="https://boards.greenhouse.io/... or https://jobs.lever.co/..."
        helperText={jobLinkSummary}
        errorText={fieldErrors.postLink || ""}
        loading={parsingLink}
        disabled={saving || parsingLink}
        buttonText="Parse"
      />

      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 2.5,
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,
          transition: "all 120ms ease",
          backgroundColor: "#fff",
          cursor: "default",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              background: "linear-gradient(135deg,#eef2ff,#f5f3ff)",
              color: "#4f46e5",
            }}
          >
            <UploadFileIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Upload File
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Upload a PDF/DOCX job description
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          component="label"
          sx={{
            textTransform: "none",
            minWidth: 120,
            borderRadius: 999,
            backgroundColor: "#7c3aed",
            color: "#fff",
            px: 2.2,
            py: 1.05,
            fontWeight: 700,
            boxShadow: "none",
            transition: "background-color 150ms ease",
            "&:hover": {
              backgroundColor: "#6d28d9",
              boxShadow: "none",
            },
          }}
        >
          <UploadFileIcon sx={{ mr: 0.75, fontSize: 18, color: "#fff" }} />
          Upload
          <input
            hidden
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              const selected = e.target.files?.[0] || null;
              if (selected) {
                setUploadFile(selected);
                handleUploadFileCreate(selected);
              }
            }}
          />
        </Button>
      </Box>

      {errorMsg ? (
        <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700 }}>
          {errorMsg}
        </Typography>
      ) : null}

      <Box sx={{ mt: 2.1 }}>
        <Divider sx={{ mb: 1.6 }} />
        <Button variant="outlined" onClick={onClose} sx={pillBtn}>
          Cancel
        </Button>
      </Box>
    </Box>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLink = () => (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>
        Add Job – Link
      </Typography>

      <TextField label="Post link" placeholder="https://..." value={postLink} onChange={(e) => setPostLink(e.target.value)} fullWidth />

      <Autocomplete
        freeSolo
        options={companies}
        loading={loadingCompanies}
        value={companyOption}
        inputValue={companyInputValue}
        onInputChange={handleCompanyInputChange}
        onChange={handleCompanyChange}
        filterOptions={filterCompanyOptions}
        getOptionLabel={getCompanyOptionLabel}
        isOptionEqualToValue={(a, b) => a.company_id === b.company_id}
        popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#a78bfa" }} />}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {option?.inputValue ? (
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {`Add "${option.inputValue}"`}
              </Typography>
            ) : (
              <>
                <Avatar src={option.company_logo || undefined} sx={{ width: 26, height: 26 }}>
                  {option.company_name?.[0]}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  <CompanyOptionLabel company={option} />
                </Typography>
              </>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Company (Client)"
            placeholder="Search company"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCompanies ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <TextField label="Select Source" placeholder="Job board / Referral / LinkedIn / etc." value={source} onChange={(e) => setSource(e.target.value)} fullWidth />

      <Box sx={{ mt: 2.1 }}>
        <Divider sx={{ mb: 1.6 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Button variant="outlined" onClick={handleBack} sx={pillBtn}>
            Back
          </Button>
          <Button variant="contained" onClick={() => goWizard(0)} sx={primaryPurpleBtn} disabled={!companyNameValue}>
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const renderLinkStep = () => (
    <Box sx={{ px: 0, pt: 0, pb: 0 }}>
      <Box sx={{ display: "grid", gap: 1.9 }}>
        <Box>
          <Typography sx={linkLabelSx}>Paste link</Typography>
          <Box
            sx={{
              ...linkSplitFieldWrapSx,
              borderColor: fieldErrors.postLink ? "#D92D20" : "#D0D5DD",
              "&:focus-within": fieldErrors.postLink
                ? {
                    borderColor: "#D92D20",
                    boxShadow: "0 0 0 3px rgba(217,45,32,0.12)",
                  }
                : linkSplitFieldWrapSx["&:focus-within"],
            }}
          >
            <Box sx={linkSplitProtocolSx}>{postProtocol}</Box>
            <TextField
              value={postUrlValue}
              onChange={(e) => handlePostLinkFieldChange(e.target.value)}
              placeholder="hourglass.com/job-info-001"
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 40,
                  px: 1.75,
                },
                "& .MuiInputBase-input": {
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#475467",
                  py: 1,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#667085",
                  opacity: 1,
                },
              }}
            />
          </Box>
          {fieldErrors.postLink ? (
            <Typography sx={{ ...fieldErrorTextSx, mt: 0.85 }}>
              {fieldErrors.postLink}
            </Typography>
          ) : (
            <Typography sx={{ ...roleHelperTextSx, mt: 0.85 }}>
              We&apos;ll fetch the posting and use OpenAI to prefill the full job form, including
              title, location, employment, compensation, description, skills, and links.
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={linkLabelSx}>Select Company (Client)</Typography>
          <Autocomplete
            freeSolo
            options={companies}
            loading={loadingCompanies}
            value={companyOption}
            inputValue={companyInputValue}
            onInputChange={handleCompanyInputChange}
            onChange={handleCompanyChange}
            filterOptions={filterCompanyOptions}
            getOptionLabel={getCompanyOptionLabel}
            isOptionEqualToValue={(a, b) => a.company_id === b.company_id}
            popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: "#344054" }}>
                  {option?.inputValue ? `Add "${option.inputValue}"` : <CompanyOptionLabel company={option} />}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select company"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={linkFieldSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography sx={linkLabelSx}>Select Source</Typography>
          <TextField
            select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            fullWidth
            sx={linkFieldSx}
            SelectProps={{ IconComponent: KeyboardArrowDownRoundedIcon }}
          >
            {LINK_SOURCE_OPTIONS.map((option) => (
              <MenuItem key={option.label} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {errorMsg ? (
          <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700, mt: -0.4 }}>
            {errorMsg}
          </Typography>
        ) : null}

        <Box sx={{ pt: 1.1, ...dialogFooterScrollSx, ...dialogFooterRowSx }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{ ...overviewBackBtnSx, minWidth: 88, px: 2.2 }}
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
            disabled={parsingLink}
          >
            Back
          </Button>

          <Box sx={dialogFooterCompactActionGroupSx}>
            <Button
              variant="outlined"
              onClick={() => goWizard(0)}
              sx={overviewOutlineBtnSx}
              disabled={parsingLink}
            >
              Continue Manually
            </Button>

            <Button
              variant="contained"
              onClick={handleParseJobLink}
              sx={{ ...overviewPrimaryBtnSx, minWidth: 146, px: 2.4}}
              disabled={!String(postUrlValue || "").trim() || parsingLink}
              startIcon={
                parsingLink ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoAwesomeIcon />
              }
            >
              {parsingLink ? "Importing..." : "Import with AI"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderUpload = () => (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>
        Add Job – Upload
      </Typography>

      <Box
        sx={{
          border: `1px dashed ${fieldErrors.uploadFile ? "#D92D20" : "#c7c9d1"}`,
          borderRadius: 2.5,
          p: 2,
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
            <UploadFileIcon sx={{ color: "#7c3aed" }} />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Upload File
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleUploadFileCreate}
            disabled={!uploadFile || processingUpload}
            startIcon={
              processingUpload ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <UploadFileIcon fontSize="small" sx={{ color: "#fff" }} />
              )
            }
            sx={{ textTransform: "none", minWidth: 140 }}
          >
            {processingUpload ? "Importing..." : "Upload & Create"}
          </Button>
        </Box>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          Upload PDF or DOCX
        </Typography>

        <Box sx={{ mt: 1.25 }}>
          <Button variant="outlined" component="label" sx={pillBtn}>
            Choose file
            <input
              hidden
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
          </Button>
        </Box>

        {uploadFile ? (
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#374151", fontWeight: 700 }}>
            Selected: {uploadFile.name}
          </Typography>
        ) : null}

        {fieldErrors.uploadFile ? (
          <Typography sx={fieldErrorTextSx}>{fieldErrors.uploadFile}</Typography>
        ) : null}
      </Box>

      <Autocomplete
        freeSolo
        options={companies}
        loading={loadingCompanies}
        value={companyOption}
        inputValue={companyInputValue}
        onInputChange={handleCompanyInputChange}
        onChange={handleCompanyChange}
        filterOptions={filterCompanyOptions}
        getOptionLabel={getCompanyOptionLabel}
        isOptionEqualToValue={(a, b) => a.company_id === b.company_id}
        popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#a78bfa" }} />}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {option?.inputValue ? `Add "${option.inputValue}"` : <CompanyOptionLabel company={option} />}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Company (Client)"
            placeholder="Search company"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCompanies ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {errorMsg ? (
        <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700, mt: 0.5 }}>
          {errorMsg}
        </Typography>
      ) : null}

      <Box sx={{ mt: 2.1, ...dialogFooterScrollSx }}>
        <Divider sx={{ mb: 1.6 }} />
        <Box sx={{ ...dialogFooterRowSx, gap: 1 }}>
          <Button variant="outlined" onClick={handleBack} sx={pillBtn}>
            Back
          </Button>
          <Box sx={dialogFooterCompactActionGroupSx}>
            <Button variant="outlined" onClick={() => goWizard(0)} sx={primaryPurpleBtn} disabled={!companyNameValue}>
              Continue
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadFileCreate}
              sx={primaryPurpleBtn}
              disabled={!uploadFile || processingUpload}
              startIcon={
                processingUpload ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <UploadFileIcon fontSize="small" sx={{ color: "#fff" }} />
                )
              }
            >
              {processingUpload ? "Importing..." : "Upload & Create Job"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderWizard = () => (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      {/* STEP 0 — Job Overview */}
      {wizardStep === 0 ? (
        <>
          <Typography variant="body2" sx={overviewTitleSx}>
            Job Overview
          </Typography>

          <Box sx={{ display: "grid", gap: 1.35, mt: 0.15 }}>
            <Box>
              <Typography sx={overviewLabelSx}>Job Title</Typography>
              <Autocomplete
                freeSolo
                options={jobTitleOptions}
                value={jobTitle}
                inputValue={jobTitle}
                onInputChange={(_, newInputValue) => {
                  setJobTitle(newInputValue);
                }}
                onChange={(_, newValue) => {
                  setJobTitle(typeof newValue === "string" ? newValue : newValue || "");
                }}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                forcePopupIcon={true}
                popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="Search or input name"
                    autoComplete="off"
                    error={Boolean(fieldErrors.jobTitle)}
                    helperText={fieldErrors.jobTitle || ""}
                    sx={overviewFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOverviewOptions ? <CircularProgress size={16} sx={{ mr: 0.25 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {isCompanyQuickAction ? (
                companyNameValue ? (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.8, color: "#667085", fontWeight: 700 }}
                  >
                    Company: {companyNameValue}
                  </Typography>
                ) : fieldErrors.company ? (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.8, color: "#b91c1c", fontWeight: 700 }}
                  >
                    {fieldErrors.company}
                  </Typography>
                ) : null
              ) : null}
            </Box>

            {!isCompanyQuickAction ? (
              <Box>
                <Typography sx={overviewLabelSx}>Company (Client)</Typography>
                <Autocomplete
                  freeSolo
                  options={companies}
                  loading={loadingCompanies}
                  value={companyOption}
                  inputValue={companyInputValue}
                  onInputChange={handleCompanyInputChange}
                  onChange={handleCompanyChange}
                  filterOptions={filterCompanyOptions}
                  getOptionLabel={getCompanyOptionLabel}
                  isOptionEqualToValue={(option, value) => option.company_id === value.company_id}
                  noOptionsText={loadingCompanies ? "Loading..." : "No companies found"}
                  forcePopupIcon={true}
                  popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                    >
                      {option?.inputValue ? (
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {`Add "${option.inputValue}"`}
                        </Typography>
                      ) : (
                        <>
                          <Avatar
                            src={option.company_logo || undefined}
                            sx={{ width: 24, height: 24 }}
                          >
                            {option.company_name?.[0]}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            <CompanyOptionLabel company={option} />
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Search or input name"
                      error={Boolean(fieldErrors.company)}
                      helperText={fieldErrors.company || ""}
                      sx={overviewFieldSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCompanies ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      />
                    )}
                  />
              </Box>
            ) : null}

            {isCompanyQuickAction ? (
              <Box>
                <Typography sx={overviewLabelSx}>Job Status</Typography>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={jobStatus}
                  onChange={(e) => setJobStatus(e.target.value)}
                  sx={overviewFieldSx}
                  SelectProps={{ IconComponent: () => null }}
                  InputProps={{ endAdornment: <DownCaretAdornment /> }}
                >
                  {COMPANY_QUICK_ACTION_JOB_STATUS_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ) : null}

            {!isCompanyQuickAction ? (
              <>
                <Box>
              <Typography sx={overviewLabelSx}>Hiring Manager/Contact</Typography>
              <Autocomplete
                freeSolo
                options={hiringManagerOptions}
                value={selectedHiringManagerOption}
                inputValue={hiringManagerName}
                onInputChange={(_, newInputValue) => {
                  setHiringManagerName(normalizeHiringManagerValue(newInputValue));
                }}
                onChange={(_, newValue) => {
                  commitHiringManagerValue(
                    typeof newValue === "string"
                      ? newValue
                      : newValue?.inputValue || newValue?.name || newValue?.label || ""
                  );
                }}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                forcePopupIcon={true}
                popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />}
                filterOptions={filterHiringManagerOptions}
                getOptionLabel={getHiringManagerOptionLabel}
                isOptionEqualToValue={(option, value) =>
                  getHiringManagerOptionKey(option) === getHiringManagerOptionKey(value)
                }
                noOptionsText={
                  selectedCompanyId
                    ? "No related contacts found for this company"
                    : "No contacts found"
                }
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                  >
                    {option?.inputValue ? (
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#344054" }}>
                        {`Add "${option.inputValue}"`}
                      </Typography>
                    ) : (
                      <>
                        <Avatar
                          src={option?.profile_image || undefined}
                          sx={{ width: 24, height: 24 }}
                        >
                          {option?.name?.[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#344054" }}>
                            {option?.name || getHiringManagerOptionLabel(option)}
                          </Typography>
                          {!!(option?.role || option?.email) && (
                            <Typography variant="caption" sx={{ color: "#667085" }}>
                              {[option?.role, option?.email].filter(Boolean).join(" | ")}
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="Search or input name"
                    error={Boolean(fieldErrors.hiringManager)}
                    helperText={fieldErrors.hiringManager || ""}
                    sx={overviewFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingHiringManagerOptions ? <CircularProgress size={16} sx={{ mr: 0.25 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
                mt: 0.15,
                alignItems: "end",
              }}
            >
              <Box>
                <Typography sx={overviewLabelSx}>
                  Location <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <LocationSearchableField
                  value={jobLocation}
                  onChange={setJobLocation}
                  placeholder="Remote or Austin, TX"
                  textFieldProps={{ sx: overviewFieldSx }}
                />
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Work Arrangement <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={workArrangement}
                  onChange={(e) => setWorkArrangement(e.target.value)}
                  sx={overviewFieldSx}
                  SelectProps={{ IconComponent: () => null }}
                  InputProps={{ endAdornment: <DownCaretAdornment /> }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {WORK_ARRANGEMENT_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Employment Type <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={employment}
                  onChange={(e) => setEmployment(e.target.value)}
                  sx={overviewFieldSx}
                  SelectProps={{ IconComponent: () => null }}
                  InputProps={{ endAdornment: <DownCaretAdornment /> }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {JOB_EMPLOYMENT_TYPE_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Slots <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={slots}
                  onChange={(e) => setSlots(e.target.value)}
                  sx={overviewFieldSx}
                  SelectProps={{ IconComponent: () => null }}
                  InputProps={{ endAdornment: <DownCaretAdornment /> }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {SLOT_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {showCandidateStageField ? (
                <Box>
                  <Typography sx={overviewLabelSx}>Stage</Typography>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    value={candidateStage}
                    onChange={(e) => setCandidateStage(e.target.value)}
                    error={Boolean(fieldErrors.candidateStage)}
                    helperText={fieldErrors.candidateStage || ""}
                    sx={overviewFieldSx}
                    SelectProps={{ IconComponent: () => null }}
                    InputProps={{ endAdornment: <DownCaretAdornment /> }}
                  >
                    {normalizedCandidateStageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ) : null}
            </Box>
              </>
            ) : null}
          </Box>

          <Box sx={{ mt: 2.6 }}>
            <Box
              sx={{
                ...dialogFooterScrollSx,
                ...(isCompanyQuickAction
                  ? { display: "flex", justifyContent: "flex-end" }
                  : dialogFooterRowSx),
              }}
            >
              {isCompanyQuickAction ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving || creatingCompany}
                  sx={{ ...overviewPrimaryBtnSx, minWidth: 116 }}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Next
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={handleBack}
                    sx={overviewBackBtnSx}
                    startIcon={<ArrowBackRoundedIcon />}
                    disabled={saving}
                  >
                    Back
                  </Button>

                  <Box sx={dialogFooterActionGroupSx}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={saving || creatingCompany}
                      sx={overviewPrimaryBtnSx}
                      endIcon={<ArrowForwardRoundedIcon />}
                    >
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </Box>

            {errorMsg ? (
              <Box sx={{ mt: 1.25 }}>
                <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700 }}>
                  {errorMsg}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      {/* STEP 1 — Role Details (NOW MATCHES THE “AddCandidate-02” FIELD SET + LAYOUT) */}
      {wizardStep === 1 ? (
        <>
          <InfoRow jobTitle={jobTitle} companyOption={companyOption} companyInputValue={companyInputValue} hiringManagerName={hiringManagerName} jobLocation={jobLocation} workArrangement={workArrangement} employment={employment} source={source} slots={slots} />

          <Typography variant="body2" sx={overviewTitleSx}>
            Role Details
          </Typography>

          {/* Upload Resume (optional) — candidate-style card */}
          <Box sx={{ display: "grid", gap: 1.5, mt: 0.15 }}>
            <Box>
              <Typography sx={overviewLabelSx}>Description</Typography>
              <TextField
                fullWidth
                multiline
                minRows={5}
                value={description}
                inputProps={{ "aria-label": "Description" }}
                onChange={(e) => setDescription(e.target.value)}
                error={Boolean(fieldErrors.description)}
                helperText={fieldErrors.description || ""}
                sx={roleTextareaSx}
              />
            </Box>

            <Box>
              <Typography sx={overviewLabelSx}>
                Responsibilities <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={responsibilities}
                inputProps={{ "aria-label": "Responsibilities" }}
                onChange={(e) => setResponsibilities(e.target.value)}
                sx={roleTextareaSx}
              />
            </Box>

            <Box>
              <Typography sx={overviewLabelSx}>
                Qualifications <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={qualifications}
                inputProps={{ "aria-label": "Qualifications" }}
                onChange={(e) => setQualifications(e.target.value)}
                sx={roleTextareaSx}
              />
            </Box>

          {/* Company (Client) */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={overviewLabelSx}>
                Skills <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>

              <Typography
                role="button"
                tabIndex={0}
                onClick={handleAddSkill}
                sx={roleActionLinkSx}
              >
                Add New
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              placeholder="JavaScript"
              sx={overviewFieldSx}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddSkill();
                }
              }}
            />

            {skills.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1 }}>
                {skills.map((s) => (
                  <Chip key={s} label={s} onDelete={() => handleRemoveSkill(s)} sx={roleChipSx} />
                ))}
              </Box>
            ) : null}
          </Box>

          {/* LinkedIn Profile URL (optional) — mapped to Job Post URL UI style */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={overviewLabelSx}>
                Soft Skills <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>

              <Typography
                role="button"
                tabIndex={0}
                onClick={handleAddSoftSkill}
                sx={roleActionLinkSx}
              >
                Add New
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={softSkillInput}
              onChange={(e) => setSoftSkillInput(e.target.value)}
              placeholder="Communication"
              sx={overviewFieldSx}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddSoftSkill();
                }
              }}
            />

            {softSkills.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1 }}>
                {softSkills.map((skill) => (
                  <Chip
                    key={skill}
                    label={skill}
                    onDelete={() => handleRemoveSoftSkill(skill)}
                    sx={roleChipSx}
                  />
                ))}
              </Box>
            ) : null}
          </Box>

          <Box>
            <Typography sx={overviewLabelSx}>
              Compensation <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={compensation}
              onChange={(e) => setCompensation(e.target.value)}
              placeholder="70k to 80k year"
              sx={overviewFieldSx}
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={overviewLabelSx}>
                Interview Process <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>

              <Typography
                role="button"
                tabIndex={0}
                onClick={handleAddInterviewStep}
                sx={roleActionLinkSx}
              >
                Add New
              </Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              value={interviewInput}
              onChange={(e) => setInterviewInput(e.target.value)}
              placeholder="Assessment"
              sx={overviewFieldSx}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddInterviewStep();
                }
              }}
            />

            <Typography sx={roleHelperTextSx}>
              Fill in the steps candidates will go through
            </Typography>

            {interviewProcess.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 0.9 }}>
                {interviewProcess.map((step) => (
                  <Chip
                    key={step}
                    label={step}
                    onDelete={() => handleRemoveInterviewStep(step)}
                    sx={roleChipSx}
                  />
                ))}
              </Box>
            ) : null}
          </Box>

          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                ...dialogFooterScrollSx,
                ...dialogFooterRowSx,
              }}
            >
              <Button
                variant="contained"
                onClick={handleBack}
                sx={overviewBackBtnSx}
                startIcon={<ArrowBackRoundedIcon />}
                disabled={saving}
              >
                Back
              </Button>

              <Box sx={dialogFooterActionGroupSx}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving}
                  sx={overviewPrimaryBtnSx}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Next
                </Button>
              </Box>
            </Box>

            {errorMsg ? (
              <Box sx={{ mt: 1.25 }}>
                <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700 }}>
                  {errorMsg}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}

      {/* STEP 2 — Additional Details (FINAL STEP MATCH) */}
      {wizardStep === 2 ? (
        <>
          <InfoRow jobTitle={jobTitle} companyOption={companyOption} companyInputValue={companyInputValue} hiringManagerName={hiringManagerName} jobLocation={jobLocation} workArrangement={workArrangement} employment={employment} source={source} slots={slots} />

          <Typography variant="body2" sx={overviewTitleSx}>
            Additional Details
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5, mt: 0.15 }}>
            <Box>
              <Typography sx={overviewLabelSx}>
                Documents <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={detailDocumentCardSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.15, minWidth: 0 }}>
                    <Box sx={detailDocumentIconWrapSx}>
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>

                    {documents?.length ? (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "#475467" }} noWrap>
                          {documents[0].name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#667085", mt: 0.1 }}>
                          {prettyBytes(documents[0]?.file?.size) || "200 KB"} - 100% uploaded
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "#475467" }} noWrap>
                          No document added
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#667085", mt: 0.1 }}>
                          Add a PDF/DOCX file
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "#9C4FFF", flex: "0 0 auto" }} />
                </Box>

                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => docsInputRef.current?.click()}
                  sx={roleActionLinkSx}
                >
                  Add New
                </Typography>

                <input
                  ref={docsInputRef}
                  hidden
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => addDocumentFiles(e.target.files)}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography sx={overviewLabelSx}>
                  Source <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="LinkedIn"
                  sx={overviewFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Job Post Link <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={postLink}
                  onChange={(e) => handlePostLinkFieldChange(e.target.value)}
                  placeholder="https://..."
                  sx={overviewFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Company Website <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={buildHttpUrl(websiteProtocol, websiteUrlValue)}
                  onChange={(e) =>
                    applySplitUrlState(
                      e.target.value,
                      setWebsiteProtocol,
                      setWebsiteUrlValue,
                      "https://"
                    )
                  }
                  placeholder="https://company.com"
                  sx={overviewFieldSx}
                />
              </Box>

              <Box>
                <Typography sx={overviewLabelSx}>
                  Career Website <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={careerWebsite}
                  onChange={(e) => setCareerWebsite(e.target.value)}
                  placeholder="https://company.com/careers"
                  sx={overviewFieldSx}
                />
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={overviewLabelSx}>
                  Other Links <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
                </Typography>

                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={handleAddOtherLink}
                  sx={roleActionLinkSx}
                >
                  Add New
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gap: 1 }}>
                {otherLinks.map((linkItem, index) => (
                  <Box data-popup-header
                    key={`${index}-${linkItem.label || linkItem.value || "link"}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "180px 1fr auto" },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      value={linkItem.label || ""}
                      onChange={(e) => handleUpdateOtherLink(index, "label", e.target.value)}
                      placeholder="Label"
                      sx={overviewFieldSx}
                    />
                    <TextField
                      size="small"
                      value={buildHttpUrl(linkItem.protocol, linkItem.value)}
                      onChange={(e) => handleUpdateOtherLink(index, "url", e.target.value)}
                      placeholder="https://..."
                      sx={overviewFieldSx}
                    />
                    <IconButton data-popup-close
                      onClick={() => handleRemoveOtherLink(index)}
                      size="small"
                      sx={{
                        color: "#6b7280",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography sx={overviewLabelSx}>
                Notes <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={roleTextareaSx}
              />
            </Box>

            <Box>
              <Typography sx={overviewLabelSx}>
                Assign Team Member <Box component="span" sx={overviewOptionalSx}>(optional)</Box>
              </Typography>

              <Autocomplete
                freeSolo
                options={teamMembers}
                loading={loadingTeamMembers}
                value={selectedTeamMember}
                inputValue={assignedTeamMember}
                onOpen={() => {
                  if (!teamMembers.length && !loadingTeamMembers) fetchTeamMembersList();
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "reset") return;
                  if (reason === "clear") {
                    handleAssignedTeamMemberChange("");
                    return;
                  }
                  handleAssignedTeamMemberChange(newInputValue || "");
                }}
                onChange={(_, newValue) => {
                  if (typeof newValue === "string") {
                    handleAssignedTeamMemberChange(newValue);
                    return;
                  }
                  handleAssignedTeamMemberChange(
                    newValue?.inputValue || newValue?.name || ""
                  );
                }}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                forcePopupIcon={true}
                popupIcon={<KeyboardArrowDownRoundedIcon sx={{ color: "#667085" }} />}
                filterOptions={(options, params) => {
                  const inputValue = normalizeTeamMemberName(params.inputValue).toLowerCase();
                  const filtered = options.filter(
                    (option) => !inputValue || option.name.toLowerCase().includes(inputValue)
                  );
                  const base = dedupeAutocompleteOptions(teamMemberFilter(filtered, {
                    ...params,
                    inputValue: "",
                  }));
                  const exists = options.some(
                    (option) => option.name.toLowerCase() === inputValue
                  );

                  if (inputValue && !exists) {
                    base.push({
                      id: null,
                      inputValue: params.inputValue,
                      name: params.inputValue,
                    });
                  }

                  return dedupeAutocompleteOptions(base, (option) =>
                    normalizeAutocompleteText(option?.name || option?.inputValue || "")
                  );
                }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option?.inputValue) return option.inputValue;
                  return String(option?.name || "");
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?.id ?? null;
                  const valueId = value?.id ?? null;

                  if (optionId != null && valueId != null) {
                    return String(optionId) === String(valueId);
                  }

                  return (
                    normalizeTeamMemberName(
                      option?.name || option?.inputValue || ""
                    ).toLowerCase() ===
                    normalizeTeamMemberName(
                      value?.name || value?.inputValue || ""
                    ).toLowerCase()
                  );
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={`${option?.id ?? "new"}-${option?.name ?? option?.inputValue ?? ""}`}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                      {option?.inputValue
                        ? `Create "${option.inputValue}"`
                        : option?.name || ""}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="Search or type a team member name"
                    sx={overviewFieldSx}
                    helperText={
                      loadingTeamMembers
                        ? "Loading team members..."
                        : teamMembers.length
                        ? "Search existing team members or type a new name to create on save."
                        : teamMembersDebug || "No team members yet. Type a name to create one on save."
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTeamMembers ? <CircularProgress size={16} sx={{ mr: 0.25 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                ...dialogFooterScrollSx,
                ...dialogFooterRowSx,
              }}
            >
              <Button
                variant="contained"
                onClick={handleBack}
                sx={overviewBackBtnSx}
                startIcon={<ArrowBackRoundedIcon />}
                disabled={saving}
              >
                Back
              </Button>

              <Box sx={dialogFooterActionGroupSx}>
                <Button
                  variant="contained"
                  onClick={handleCreateJob}
                  disabled={saving}
                  sx={{ ...overviewPrimaryBtnSx, minWidth: 116 }}
                >
                  {createButtonLabel}
                </Button>
              </Box>
            </Box>

            {errorMsg ? (
              <Box sx={{ mt: 1.25 }}>
                <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700 }}>
                  {errorMsg}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 24px 60px rgba(15,23,42,0.25)" } }}
    >
      <Header
        dialogTitle={dialogTitle}
        screen={screen}
        wizardStep={wizardStep}
        onClose={onClose}
        showWizardSteps
      />
      <DialogContent sx={{ px: 4, pt: 0, pb: 4 }}>
        {screen === "menu" ? renderMenu() : null}
        {screen === "link" ? renderLinkStep() : null}
        {screen === "upload" ? renderUpload() : null}
        {screen === "wizard" ? renderWizard() : null}
      </DialogContent>
    </Dialog>
  );
}
